import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/styles';
import {
  Box,
  IconButton,
  createMuiTheme,
  ThemeProvider,
  ListItem,
  List,
  Collapse,
  Button,
  Menu,
  MenuItem,
  styled,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  LockOpen,
  ArrowUpward,
  ArrowDownward,
  Edit,
  ExpandLess,
  ExpandMore,
  ArrowBackIosTwoTone,
  ArrowForwardIosTwoTone,
  Launch,
  InfoOutlined,
  DeleteOutline,
  Add,
} from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';
import Lock from '@material-ui/icons/Lock';
import {
  getChapterIndex,
  getParagraphIndex,
  setChapterIndex,
  setParagraphIndex,
} from '../../../utils/storage';
import {
  getContenutoParagrafo,
  getParagrafiManualePersonalizzato,
  postAggiuntaPDFForParagrafo,
  postUploadImmaginiSottoparagrafi,
  putModificaParagrafiManualePersonalizzato,
  updateSottoparagrafi,
} from '../../../utils/api/paragrafi_api';
import {
  getCapitoli,
  getCapitoliCount,
  putCapitoli,
} from '../../../utils/api/capitoli_api';
import { getManuale } from '../../../utils/api/modelli_and_manuali_api';
import { updateReparti } from '../../../utils/api/reparti';
import { updateLocaliManuale } from '../../../utils/api/locali_e_attrezzature_api';
import CustomTableForManuale from './CustomTableForManuale';
import ActionButton from '../../../components/ActionButton';
import NewSottoparagrafoTesto from './NewSottoparagrafoTesto';
import NewSottoparagrafoTestoEImmagine from './NewSottoparagrafoTestoEImmagine';
import ErrorDialog from '../../../components/ErrorDialog';
import SpinnerComponent from '../../../components/SpinnerComponent';
import ContentBlocksSkeleton from '../../../components/ContentBlocksSkeleton';
import ModalChangeChapterName from './ModalChangeChapterName';
import EmptyManualView from './EmptyManualView';
import TwoActionsAlertDialogForFileUpload from '../../../components/TwoActionsAlertDialogForFileUpload';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { Document, Page, pdfjs } from 'react-pdf';
import CreazioneTabellaModal from './CreazioneTabellaModal';
import IndietroButtonColorFul from '../../../components/IndietroButtonColorful';
import UnsavedChangesNavigationGuard from '../../../components/UnsavedChangesNavigationGuard.jsx';
import { UNSAVED_CHANGES_LEAVE_MESSAGE } from '../../../utils/messages';
import { skipNextNavigationGuard } from '../../../utils/useUnsavedChangesNavigationGuard';
import { reportApiError, reportError } from '../../../utils/reportApiError';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const styles = (theme) => {
  /**
   * Shared vertical baseline: `editorPanel` uses it as minHeight, `navigationPanel` only as
   * maxHeight (scroll cap). The nav card must NOT be pinned to this height, otherwise with only a
   * few paragraphs a large gap is left below the last item: it follows its content and scrolls only
   * once it exceeds the cap.
   * Empirical `theme.spacing(105)` chosen to match the left "Struttura manuale" card (header, scroll,
   * footer actions) once real content is present. The right card is never modeled as empty: there is
   * always at least one paragraph in play, so the baseline includes chrome (padding, h6, title row,
   * "Sottoparagrafi" section, actions) plus room for that first block. Change the multiplier only if
   * the layout drifts.
   */
  const baselineManualPanelHeight = theme.spacing(105);
  const structureNavTextMaxLines = 6;
  const structureNavTextLineHeight = 1.4;
  const structureNavTextTruncation = {
    display: '-webkit-box',
    WebkitLineClamp: structureNavTextMaxLines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: structureNavTextLineHeight,
    maxHeight: `${structureNavTextMaxLines * structureNavTextLineHeight}em`,
  };

  return {
    mainContainer: {
      color: theme.palette.text.primary,
      padding: theme.spacing(4, 3),
      minHeight: '90vh',
      backgroundColor: theme.palette.background.default,
    },
    title: {
      marginBottom: 0,
      fontWeight: 700,
    },
    root: {
      display: 'grid',
      gridTemplateColumns: 'minmax(400px, 420px) minmax(0, 1fr)',
      gap: theme.spacing(3),
      alignItems: 'start',
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
      },
    },
    errorPDFPosition: {
      position: 'relative',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    pageShell: {
      maxWidth: 1680,
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
    },
    pageHeader: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      textAlign: 'left',
    },
    panelCard: {
      backgroundColor: theme.palette.background.paper,
      border: '1px solid rgba(15, 23, 42, 0.08)',
      borderRadius: 20,
      boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
    },
    navigationPanel: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      padding: theme.spacing(3),
      minWidth: 0,
      minHeight: 0,
      // Height driven by content; the baseline is only the cap beyond which it scrolls.
      height: 'auto',
      maxHeight: baselineManualPanelHeight,
      alignSelf: 'start',
      overflow: 'hidden',
    },
    navigationScrollArea: {
      // `1 1 auto`: with an auto basis the list is not stretched to fill the card.
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      paddingBottom: theme.spacing(0.5),
      // Keeps the card content clear of the scrollbar
      paddingRight: theme.spacing(1.5),
      boxSizing: 'border-box',
      WebkitOverflowScrolling: 'touch',
    },
    navigationPanelHeader: {
      flexShrink: 0,
    },
    navigationPanelFooter: {
      flexShrink: 0,
      marginTop: 'auto',
    },
    editorPanel: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
      padding: theme.spacing(3),
      minWidth: 0,
      minHeight: baselineManualPanelHeight,
    },
    panelHeader: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.75),
      textAlign: 'left',
    },
    panelTitle: {
      fontWeight: 700,
    },
    panelTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.25),
    },
    panelInfoButton: {
      padding: theme.spacing(0.5),
      marginLeft: theme.spacing(-0.25),
      color: theme.palette.text.secondary,
    },
    structureInfoTooltip: {
      maxWidth: 320,
      fontSize: theme.typography.pxToRem(12),
      lineHeight: 1.5,
    },
    chapterList: {
      width: '100%',
      paddingTop: 0,
      paddingBottom: theme.spacing(1),
    },
    chapterListInner: {
      width: '100%',
      padding: theme.spacing(1.25, 0, 0, 1.5),
      boxSizing: 'border-box',
    },
    chapterParagraphActions: {
      width: '100%',
      padding: theme.spacing(1, 0, 2, 1.5),
      boxSizing: 'border-box',
    },
    navItem: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      border: '1px solid rgba(15, 23, 42, 0.08)',
      borderRadius: 8,
      padding: theme.spacing(0.75, 0),
      marginBottom: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
      overflow: 'hidden',
      '&.MuiListItem-button': {
        alignItems: 'stretch',
      },
    },
    navItemContent: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      width: '100%',
      minWidth: 0,
      overflow: 'hidden',
    },
    navItemHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      flex: 1,
      minWidth: 0,
      paddingLeft: theme.spacing(0.5),
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75),
      overflow: 'hidden',
    },
    navExpandSlot: {
      width: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    navItemExpandIcon: {
      color: theme.palette.text.secondary,
    },
    navItemMain: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'flex-start',
      overflow: 'hidden',
    },
    navItemTitleBlock: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    navItemTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.25),
      minWidth: 0,
      overflow: 'hidden',
    },
    navItemEditButton: {
      padding: theme.spacing(0.25),
      flexShrink: 0,
    },
    navItemText: {
      ...structureNavTextTruncation,
      flex: 1,
      width: '100%',
      minWidth: 0,
      fontWeight: 700,
      textAlign: 'left',
    },
    navSubItem: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      border: '1px solid rgba(15, 23, 42, 0.08)',
      borderRadius: 8,
      padding: theme.spacing(0.75, 0),
      marginBottom: theme.spacing(0.75),
      backgroundColor: theme.palette.background.paper,
      boxSizing: 'border-box',
      overflow: 'hidden',
      '&.MuiListItem-button': {
        alignItems: 'stretch',
      },
    },
    navSubItemContentWrap: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      width: '100%',
      minWidth: 0,
      overflow: 'hidden',
    },
    navSubItemSelected: {
      backgroundColor: 'rgba(63, 81, 181, 0.08)',
    },
    navSubItemMain: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'stretch',
      overflow: 'hidden',
    },
    navSubItemContent: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      overflow: 'hidden',
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75),
    },
    navSubItemText: {
      ...structureNavTextTruncation,
      flex: 1,
      width: '100%',
      minWidth: 0,
      textAlign: 'left',
    },
    selectionSlot: {
      width: 3,
      alignSelf: 'stretch',
      flexShrink: 0,
      marginRight: theme.spacing(1),
      borderRadius: 999,
    },
    selectionSlotActive: {
      backgroundColor: theme.palette.primary.main,
    },
    selectionSlotInactive: {
      backgroundColor: 'transparent',
    },
    navActionsColumn: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(0.25),
      flexShrink: 0,
      alignSelf: 'center',
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      marginLeft: theme.spacing(1),
    },
    navActionSlot: {
      width: 32,
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    /**
     * The wireframe renders "move down / move up" as a single compact group (↓↑).
     * They stay two separate buttons so neither action is lost.
     */
    navMoveGroup: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      flexShrink: 0,
    },
    navMoveButton: {
      padding: theme.spacing(0.25),
    },
    navLeadingSlot: {
      width: 32,
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    navActionButton: {
      padding: theme.spacing(0.5),
    },
    utilityActionIcon: {
      color: theme.palette.text.secondary,
    },
    destructiveActionIcon: {
      color: theme.palette.error.main,
    },
    /**
     * Single add affordance (paragraph or PDF): dark square with a "+",
     * opens the menu holding both actions.
     */
    addIconButton: {
      width: 40,
      height: 40,
      borderRadius: 8,
      padding: 0,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
    },
    addIconButtonRow: {
      display: 'flex',
      justifyContent: 'flex-start',
      width: '100%',
    },
    navigationActions: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(1),
      width: '100%',
    },
    secondaryActionButton: {
      justifyContent: 'center',
      borderRadius: 8,
      width: '90%',
      padding: theme.spacing(0.875, 1.75),
      textTransform: 'none',
      fontWeight: 600,
    },
    /**
     * Editor panel variant: width follows content, not 90% like
     * `secondaryActionButton` (which targets the navigation column).
     */
    editorActionButton: {
      justifyContent: 'center',
      borderRadius: 8,
      padding: theme.spacing(0.875, 1.75),
      textTransform: 'none',
      fontWeight: 600,
    },
    textActionButton: {
      justifyContent: 'center',
      borderRadius: 8,
      width: '90%',
      padding: theme.spacing(0.875, 1.5),
      textTransform: 'none',
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
    tabsContainer: {
      borderRight: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down('md')]: {
        borderRight: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
        paddingBottom: theme.spacing(2),
      },
    },
    verticalTab: {
      maxWidth: '100%',
      height: 'auto',
      minHeight: 'unset',
      overflow: 'visible',
      alignItems: 'stretch',
      paddingLeft: 0,
      paddingRight: 0,
      textTransform: 'none',
    },
    tabLabel: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      width: '100%',
      minWidth: 0,
      padding: theme.spacing(0.75, 0),
      overflow: 'hidden',
    },
    tabLabelContent: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      overflow: 'hidden',
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75),
    },
    tabLabelText: {
      ...structureNavTextTruncation,
      flex: 1,
      width: '100%',
      minWidth: 0,
      fontSize: '0.95rem',
      textAlign: 'left',
    },
    editorHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    editorTitleField: {
      flex: 1,
    },
    editorHeaderActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    sectionBlock: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    sectionHeader: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      textAlign: 'left',
    },
    sectionTitle: {
      fontWeight: 700,
    },
    contentContainer: {
      border: '1px solid rgba(15, 23, 42, 0.08)',
      backgroundColor: 'rgba(15, 23, 42, 0.02)',
      borderRadius: 16,
      padding: theme.spacing(3),
    },
    contentBox: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    contextualActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1.5),
    },
    pdfSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    pdfActionRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1.5),
      alignItems: 'center',
    },
    primaryLinkButton: {
      borderRadius: 8,
      padding: theme.spacing(1.25, 1.75),
      textTransform: 'none',
      fontWeight: 600,
      color: theme.palette.text.main,
      backgroundColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
      },
    },
    pdfActionRowUniformButton: {
      boxSizing: 'border-box',
      minHeight: theme.spacing(5),
      padding: theme.spacing(1.25, 1.75),
    },
    pdfViewerRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(1),
      },
    },
    pdfPagerButton: {
      border: '1px solid rgba(15, 23, 42, 0.08)',
      backgroundColor: theme.palette.background.paper,
    },
    pdfPreviewWrapper: {
      maxWidth: '100%',
      overflow: 'auto',
      padding: theme.spacing(1),
    },
    pdfPreviewFrame: {
      border: '1px solid #e0e0e0',
      borderRadius: 14,
      boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)',
      overflow: 'hidden',
      backgroundColor: theme.palette.background.paper,
    },
    pdfPageCount: {
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    footerBar: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: theme.spacing(2),
      border: '1px solid rgba(15, 23, 42, 0.08)',
      borderRadius: 20,
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
    },
    footerActions: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    },
  };
};

function swapChapterElements(arr, i1, i2) {
  let temp = arr[i1];
  arr[i1] = arr[i2];

  arr[i2] = temp;
  arr[i1].paragraphData.forEach((item) => {
    item.chapterIndex = i1;
  });
  arr[i2].paragraphData.forEach((item) => {
    item.chapterIndex = i2;
  });
}

function getIdToSave(paragraphs) {
  return paragraphs && paragraphs.length > 0 ? paragraphs[0].id : 0;
}

function getEmptyCurrentTabValue(idManuale) {
  return {
    label: '',
    nomeTab: '',
    value: '',
    id: null,
    idManuale: idManuale || '',
    isSelected: true,
    isBlocked: false,
    obbligatorio: false,
    tipo: 'Testo',
    pdfId: null,
  };
}

function swapTabElements(arr, i1, i2) {
  let valueOfFirst = arr[i1].value;
  let valueOfLast = arr[i2].value;

  let temp = arr[i1];

  arr[i1] = arr[i2];

  arr[i2] = temp;

  arr[i1].value = valueOfFirst;
  arr[i2].value = valueOfLast;
}

const BASE_URL = process.env.REACT_APP_BACKEND_ENDPOINT;

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function StructureNavLabel({
  text,
  className,
  tooltipClassName,
  typographyProps,
}) {
  const textRef = useRef(null);
  const rafRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const label = text != null ? String(text) : '';

  const checkTruncation = useCallback(() => {
    const element = textRef.current;
    if (!element) {
      setIsTruncated(false);
      return;
    }

    setIsTruncated(
      element.scrollHeight > element.clientHeight + 1 ||
        element.scrollWidth > element.clientWidth + 1,
    );
  }, []);

  /**
   * Measuring inside the ResizeObserver callback triggers
   * "ResizeObserver loop completed with undelivered notifications": the setState
   * reads layout back in the same notification cycle. Defer to the next frame.
   */
  const scheduleCheckTruncation = useCallback(() => {
    if (typeof window === 'undefined' || !window.requestAnimationFrame) {
      checkTruncation();
      return;
    }
    if (rafRef.current != null) {
      window.cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      checkTruncation();
    });
  }, [checkTruncation]);

  useEffect(() => {
    scheduleCheckTruncation();
  }, [label, scheduleCheckTruncation]);

  useEffect(() => {
    const element = textRef.current;
    const cleanupRaf = () => {
      if (rafRef.current != null && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    if (!element || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', scheduleCheckTruncation);
      return () => {
        window.removeEventListener('resize', scheduleCheckTruncation);
        cleanupRaf();
      };
    }

    const observer = new ResizeObserver(scheduleCheckTruncation);
    observer.observe(element);
    window.addEventListener('resize', scheduleCheckTruncation);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', scheduleCheckTruncation);
      cleanupRaf();
    };
  }, [scheduleCheckTruncation]);

  return (
    <Tooltip
      title={label}
      placement="bottom"
      enterTouchDelay={0}
      disableHoverListener={!isTruncated || label.trim() === ''}
      classes={{ tooltip: tooltipClassName }}
    >
      <span
        style={{
          display: 'block',
          minWidth: 0,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Typography
          ref={textRef}
          display="block"
          className={className}
          {...typographyProps}
        >
          {text}
        </Typography>
      </span>
    </Tooltip>
  );
}

StructureNavLabel.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  tooltipClassName: PropTypes.string,
  typographyProps: PropTypes.object,
};

StructureNavLabel.defaultProps = {
  text: '',
  className: undefined,
  tooltipClassName: undefined,
  typographyProps: {},
};

function NavActionButton({
  title,
  ariaLabel,
  onClick,
  disabled,
  children,
  className,
}) {
  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (onClick) {
      onClick(event);
    }
  };

  const button = (
    <IconButton
      size="small"
      className={className}
      aria-label={ariaLabel || title}
      disabled={disabled}
      onClick={handleClick}
    >
      {children}
    </IconButton>
  );

  return (
    <Tooltip title={title} placement="top" enterTouchDelay={0}>
      {disabled ? <span>{button}</span> : button}
    </Tooltip>
  );
}

NavActionButton.propTypes = {
  title: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

NavActionButton.defaultProps = {
  ariaLabel: undefined,
  onClick: undefined,
  disabled: false,
  className: undefined,
};

export class CreazioneNuovoManuale extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tabValue: 0,
      idToSave: 0,
      idManuale: 0,
      currentTabValue: {
        label: '',
        value: '',
        idManuale: '',
        isSelected: true,
        isBlocked: true,
        obbligatorio: true,
        tipo: 'Testo',
      },
      tabListValues: [
        /*{
        name:"Chapter 1",
        isTabOpen:false,
        paragraphData:[{
          nomeTab:"Organigramma",
          value:0,
          isTabSelected:false,
        },{
          nomeTab:"Definizione Procedure Autocontrollo",
          value:1,
          isTabSelected:false,
        }]
        },
        {
          name:"Chapter 2",
          isTabOpen:false,
          paragraphData:[{
            nomeTab:"Descrizione Reparti",
            value:2,
            isTabSelected:false,
          },{
            nomeTab:"Definizione Reparti",
            value:3,
            isTabSelected:false,
          }]
          },*/
      ],
      loading: true,
      edit: false,
      model: [],
      manual: [],
      tabs: [
        /*{label:"Organigramma",value:"Organigramma",isSelected:true,isBlocked:true},{label:"Definizione Reparti",value:"Definizione_Reparti",isSelected:true,isBlocked:true},{label:"Descrizione Reparti",value:"Descrizione_Reparti",isSelected:true,isBlocked:true}/*,{label:"Attrezzatura",value:"Attrezzatura",isSelected:true,isBlocked:true},{label:"Definizione Procedure Autocontrollo",value:"Descrizione_Procedure_Autocontrollo",isSelected:true,isBlocked:true}*/
      ],
      isModalOpen: false,
      isSottoparagrafiModalOpen: false,
      firstRefresh: true,
      isDialogOpen: false,
      tipoDiTabella: 'Custom',
      tabelle: [],
      tabelleIndex: 1,
      chapterMode: undefined,
      colonneTabellaCustom: [],
      numberOfSottoparagrafo: 0,
      sottoParagrafi: [],
      sottoParagrafiData: [],
      sottoParagrafoIndex: 0,
      numberOfParagrafi: 0,
      selectedChapter: null,
      selectedChapterIndex: null,
      titoloSottoParagrafo: 'Nuovo Sottoparagrafo',
      modalitaSottoparagrafo: 'Testo',
      numberOfLines: 1,
      isChangeChapterNameModalOpen: false,
      modalTitle: '',
      editParagraphTitle: false,
      showTables: false,
      updatedReparti: null,
      updatedLocali: null,
      isConadModalOpen: false,
      manualeInfo: null,
      isTabChangePressed: false,
      isSaveButtonPressed: false,
      twoActionAlert: {
        isVisible: false,
        title: '',
        message: '',
        message2: '',
        indexForCapitolo: null,
        loading: false,
      },
      isTwoActionAlertForUpdateVisible: false,
      isTwoActionAlertForUpdateLoading: false,
      fileForUpdatePDF: null,
      newParagraphFilePDF: null,
      pdfLoadedPage: 1,
      pdfNumberOfPages: 1,
      modifiedPDFFile: null,
      modifiedPDFFileUrl: null,
      loadingPDF: false,
      paragraphBlockState: false,
      sottoparagrafiLoading: false,
      // Menu of the "+" button: chapterIndex null = mode without chapters
      addParagraphMenu: {
        anchorEl: null,
        chapterIndex: null,
      },
    };
  }

  componentDidMount() {
    this.isConad();
    if (this.state.firstRefresh) {
      this.handleRefresh();
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  componentWillUnmount() {
    if (this.state.modifiedPDFFileUrl) {
      URL.revokeObjectURL(this.state.modifiedPDFFileUrl);
    }
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  handleBeforeUnload = (event) => {
    if (this.state.edit) {
      event.preventDefault();
      event.returnValue = UNSAVED_CHANGES_LEAVE_MESSAGE;
    }
  };

  handlePushBack = () => {
    const { history } = this.props;
    history.goBack();
  };

  openConadHandlingModal = () => {
    this.setState({ isConadModalOpen: true });
  };

  isConad = () => {
    getManuale(this.props.manualeId)
      .then((manuale) => {
        if (manuale.gruppoId === 101) {
          this.openConadHandlingModal();
        } else {
          this.getParagrafiManualeInCostruzione();
        }
      })
      .catch((error) => {
        reportApiError(error);
      });
  };

  handleRefresh = () => {
    this.setState({ firstRefresh: false });
  };

  hasSelectedParagraphForEditor = () => {
    return this.state.currentTabValue.id != null;
  };

  getPlaceholderChapterName = () => {
    if (!this.state.chapterMode) {
      return null;
    }
    const openChapter = this.state.tabListValues.find(
      (chapter) => chapter.isTabOpen,
    );
    if (openChapter) {
      return openChapter.name;
    }
    const chapterWithParagraphs = this.state.tabListValues.find(
      (chapter) => chapter.paragraphData.length > 0,
    );
    if (chapterWithParagraphs) {
      return chapterWithParagraphs.name;
    }
    return this.state.tabListValues[0]?.name ?? null;
  };

  clearParagraphEditorSelection = () => {
    this.setState({
      loading: false,
      loadingPDF: false,
      paragraphBlockState: false,
      tabValue: -1,
      modalTitle: '',
      currentTabValue: getEmptyCurrentTabValue(
        this.props.manualeId || this.state.idManuale,
      ),
      sottoParagrafi: [],
      sottoParagrafiData: [],
      edit: false,
      editParagraphTitle: false,
      modifiedPDFFile: null,
      modifiedPDFFileUrl: null,
    });
  };

  selectParagraphAfterReload = (
    preferredChapterIndex,
    preferredParagraphIndex,
  ) => {
    if (this.state.chapterMode) {
      if (
        preferredChapterIndex != null &&
        this.state.tabListValues[preferredChapterIndex]?.paragraphData?.length >
          0
      ) {
        const paragraphData =
          this.state.tabListValues[preferredChapterIndex].paragraphData;
        const paragraphIndex =
          preferredParagraphIndex != null &&
          preferredParagraphIndex < paragraphData.length
            ? preferredParagraphIndex
            : 0;
        this.handleTabChangeNew(paragraphIndex, preferredChapterIndex);
        return;
      }
      for (let i = 0; i < this.state.tabListValues.length; i++) {
        if (this.state.tabListValues[i].paragraphData.length > 0) {
          this.handleTabChangeNew(0, i);
          return;
        }
      }
      this.clearParagraphEditorSelection();
      return;
    }
    if (this.state.tabs.length > 0) {
      const paragraphIndex =
        preferredParagraphIndex != null &&
        preferredParagraphIndex < this.state.tabs.length
          ? preferredParagraphIndex
          : 0;
      this.handleTabChangeManually(paragraphIndex);
      return;
    }
    this.clearParagraphEditorSelection();
  };

  sortByKey = (array) => {
    return array.sort(function (a, b) {
      let x = a['value'];
      let y = b['value'];
      return x < y ? -1 : x > y ? 1 : 0;
    });
  };

  getParagrafiManualeInCostruzione = () => {
    const { manualeId } = this.props;
    let numberOfChapters = 0;
    let chapter = [];
    getCapitoliCount(manualeId)
      .then((result) => {
        numberOfChapters = result;
      })
      .then(() => {
        if (numberOfChapters === 0 && this.state.chapterMode === true) {
          return getParagrafiManualePersonalizzato(manualeId).then((result) => {
            let tabArray = [];
            for (const item of result) {
              tabArray.push({
                label: item.titolo,
                nomeTab: item.titolo,
                isTabSelected: false,
                id: item.id,
                stato: item.stato,
                value: item.numeroParagrafo,
                idManuale: item.manualeId,
                isSelected: true,
                isBlocked: item.bloccato,
                obbligatorio: item.obbligatorio,
                tipo: item.type === null ? 'Testo' : item.type,
                pdfId: item.pdfId !== undefined ? item.pdfId : null,
              });
            }
            this.setState(
              {
                tabs: tabArray,
                tabListValues: [],
                idToSave: getIdToSave(result),
                idManuale: this.props.manualeId,
                numberOfParagrafi: tabArray.length,
                chapterMode: true,
              },
              () => {
                this.clearParagraphEditorSelection();
              },
            );
          });
        } else if (numberOfChapters > 0) {
          let tabArray = [];
          return getCapitoli(manualeId).then((result) => {
            result.forEach((item, index) => {
              tabArray = [];
              const chapterToPush = {
                name: item.capitolo.titolo,
                id: item.capitolo.id,
                isTabOpen: false,
                paragraphData: [],
              };

              item.paragrafi.forEach((paragrafo) => {
                tabArray.push({
                  label: paragrafo.titolo,
                  nomeTab: paragrafo.titolo,
                  isTabSelected: false,
                  id: paragrafo.id,
                  stato: paragrafo.stato,
                  value: paragrafo.numeroParagrafo,
                  idManuale: paragrafo.manualeId,
                  chapterIndex: index,
                  isSelected: true,
                  isBlocked: paragrafo.bloccato,
                  obbligatorio: paragrafo.obbligatorio,
                  tipo: paragrafo.type === null ? 'Testo' : paragrafo.type,
                  pdfId: paragrafo.pdfId !== undefined ? paragrafo.pdfId : null,
                });
              });
              chapterToPush.paragraphData = tabArray;
              this.sortByKey(tabArray);
              chapter.push(chapterToPush);
            });
            this.setState(
              {
                tabs: tabArray,
                tabListValues: chapter,
                idToSave: getIdToSave(tabArray),
                idManuale: manualeId,
                numberOfParagrafi: tabArray.length,
                chapterMode: true,
              },
              () => {
                this.selectParagraphAfterReload(
                  parseInt(getChapterIndex(), 10),
                  parseInt(getParagraphIndex(), 10),
                );
              },
            );
          });
        } else {
          let indexToChange = parseInt(getParagraphIndex(), 10);
          this.state.tabs.forEach((tab, index) => {
            if (tab.id === this.state.currentTabValue.id) {
              indexToChange = index;
            }
          });

          getParagrafiManualePersonalizzato(manualeId)
            .then((result) => {
              let tabArray = [];
              for (let i = 0; i < result.length; i++) {
                tabArray.push({
                  label: result[i].titolo,
                  id: result[i].id,
                  stato: result[i].stato,
                  value: result[i].numeroParagrafo,
                  idManuale: result[i].manualeId,
                  isSelected: true,
                  isBlocked: result[i].bloccato,
                  obbligatorio: result[i].obbligatorio,
                  tipo: result[i].type === null ? 'Testo' : result[i].type,
                  pdfId: result[i].pdfId !== undefined ? result[i].pdfId : null,
                });
              }
              this.sortByKey(tabArray);
              this.setState(
                {
                  tabs: tabArray,
                  idToSave: getIdToSave(result),
                  idManuale:
                    result.length > 0
                      ? result[0].manualeId
                      : this.props.manualeId,
                  numberOfParagrafi: tabArray.length,
                  chapterMode: false,
                },
                () => {
                  this.selectParagraphAfterReload(null, indexToChange);
                },
              );
            })
            .catch((error) => {
              reportApiError(error);
            });
        }
      })
      .catch((error) => {
        reportApiError(error);
      });
  };

  getParagrafiManualeInCostruzioneAfterSaveForChapters = () => {
    const manualeid = this.props.manualeId;
    let numberOfChapters = 0;
    let chapter = [];
    let chapterToPush = {};
    getCapitoliCount(manualeid)
      .then((result) => {
        numberOfChapters = result;
      })
      .then(() => {
        if (numberOfChapters === 0 && this.state.chapterMode === true) {
          const manualeid = this.props.manualeId;
          return getParagrafiManualePersonalizzato(manualeid).then((result) => {
            let tabArray = [];
            for (let i = 0; i < result.length; i++) {
              tabArray.push({
                label: result[i].titolo,
                nomeTab: result[i].titolo,
                isTabSelected: false,
                id: result[i].id,
                stato: result[i].stato,
                value: result[i].numeroParagrafo,
                idManuale: result[i].manualeId,
                isSelected: true,
                isBlocked: result[i].bloccato,
                obbligatorio: result[i].obbligatorio,
                tipo: result[i].type === null ? 'Testo' : result[i].type,
                pdfId: result[i].pdfId !== undefined ? result[i].pdfId : null,
              });
            }
            this.setState(
              {
                tabs: tabArray,
                tabListValues: [],
                idToSave: getIdToSave(result),
                idManuale: this.props.manualeId,
                numberOfParagrafi: tabArray.length,
                chapterMode: true,
              },
              () => {
                this.clearParagraphEditorSelection();
              },
            );
          });
        } else if (numberOfChapters > 0) {
          let tabArray = [];
          return getCapitoli(manualeid).then((result) => {
            result.forEach((item, index) => {
              tabArray = [];
              chapterToPush = {
                name: item.capitolo.titolo,
                id: item.capitolo.id,
                isTabOpen: this.state.tabListValues[index]
                  ? this.state.tabListValues[index].isTabOpen
                  : false,
                paragraphData: [],
              };
              for (let i = 0; i < item.paragrafi.length; i++) {
                tabArray.push({
                  label: item.paragrafi[i].titolo,
                  nomeTab: item.paragrafi[i].titolo,
                  isTabSelected: false,
                  id: item.paragrafi[i].id,
                  stato: item.paragrafi[i].stato,
                  value: item.paragrafi[i].numeroParagrafo,
                  idManuale: item.paragrafi[i].manualeId,
                  chapterIndex: index,
                  isSelected: true,
                  isBlocked: item.paragrafi[i].bloccato,
                  obbligatorio: item.paragrafi[i].obbligatorio,
                  tipo:
                    item.paragrafi[i].type === null
                      ? 'Testo'
                      : item.paragrafi[i].type,
                  pdfId:
                    item.paragrafi[i].pdfId !== undefined
                      ? item.paragrafi[i].pdfId
                      : null,
                });
                chapterToPush.paragraphData = tabArray;
              }
              this.sortByKey(tabArray);
              chapter.push(chapterToPush);
            });
            this.setState(
              {
                tabs: tabArray,
                tabListValues: chapter,
                idToSave: getIdToSave(tabArray),
                idManuale: this.props.manualeId,
                numberOfParagrafi: tabArray.length,
                chapterMode: true,
              },
              () => {
                const chapterIndex = this.state.currentTabValue.chapterIndex;
                const paragraphIndex =
                  this.state.currentTabValue.value !== '' &&
                  this.state.currentTabValue.value != null
                    ? this.state.currentTabValue.value - 1
                    : null;
                this.selectParagraphAfterReload(chapterIndex, paragraphIndex);
              },
            );
          });
        } else {
          let indexToChange = 0;
          this.state.tabs.forEach((tab, index) => {
            if (tab.id === this.state.currentTabValue.id) {
              indexToChange = index;
            }
          });
          const manualeid = this.props.manualeId;
          getParagrafiManualePersonalizzato(manualeid)
            .then((result) => {
              let tabArray = [];
              for (let i = 0; i < result.length; i++) {
                tabArray.push({
                  label: result[i].titolo,
                  id: result[i].id,
                  stato: result[i].stato,
                  value: result[i].numeroParagrafo,
                  idManuale: result[i].manualeId,
                  isSelected: true,
                  isBlocked: result[i].bloccato,
                  obbligatorio: result[i].obbligatorio,
                  tipo: result[i].type === null ? 'Testo' : result[i].type,
                  pdfId: result[i].pdfId !== undefined ? result[i].pdfId : null,
                });
              }
              this.sortByKey(tabArray);
              this.setState(
                {
                  tabs: tabArray,
                  idToSave: getIdToSave(result),
                  idManuale:
                    result.length > 0
                      ? result[0].manualeId
                      : this.props.manualeId,
                  numberOfParagrafi: tabArray.length,
                  chapterMode: false,
                },
                () => {
                  this.selectParagraphAfterReload(null, indexToChange);
                },
              );
            })
            .catch((error) => {
              reportApiError(error);
            });
        }
      })
      .catch((error) => {
        reportApiError(error);
      });
  };

  getParagrafiManualeInCostruzioneAfterSave = () => {
    let indexToChange = 0;
    this.state.tabs.forEach((tab, index) => {
      if (tab.id === this.state.currentTabValue.id) {
        indexToChange = index;
      }
    });
    const manualeid = this.props.manualeId;
    getParagrafiManualePersonalizzato(manualeid)
      .then((result) => {
        let tabArray = [];
        for (let i = 0; i < result.length; i++) {
          tabArray.push({
            label: result[i].titolo,
            id: result[i].id,
            stato: result[i].stato,
            value: result[i].numeroParagrafo,
            idManuale: result[i].manualeId,
            isSelected: true,
            isBlocked: result[i].bloccato,
            obbligatorio: result[i].obbligatorio,
            tipo: result[i].type === null ? 'Testo' : result[i].type,
            pdfId: result[i].pdfId !== undefined ? result[i].pdfId : null,
          });
        }
        this.sortByKey(tabArray);
        this.setState(
          {
            tabs: tabArray,
            idToSave: getIdToSave(result),
            idManuale:
              result.length > 0 ? result[0].manualeId : this.props.manualeId,
            numberOfParagrafi: tabArray.length,
            chapterMode: false,
          },
          () => {
            this.selectParagraphAfterReload(null, indexToChange);
          },
        );
      })
      .catch((error) => {
        reportApiError(error);
      });
  };

  handleAggiuntaCapitolo = () => {
    if (this.state.edit) {
      let confirmation = window.confirm('Modifiche non salvate, continuare?');
      if (confirmation) {
        let arrayToCheck = [...this.state.tabListValues];
        if (arrayToCheck.length === 0) {
          let oldParagraphs = [...this.state.tabs];
          let paragraphToInsert = [];
          oldParagraphs.forEach((_item, index) => {
            paragraphToInsert.push({
              label: oldParagraphs[index].label,
              nomeTab: oldParagraphs[index].label,
              isTabSelected:
                this.state.currentTabValue.value - 1 === index ? true : false,
              id: oldParagraphs[index].id,
              stato: oldParagraphs[index].stato,
              value: oldParagraphs[index].value,
              idManuale: oldParagraphs[index].idManuale,
              chapterIndex: 0,
              isSelected: true,
              isBlocked: oldParagraphs[index].isBlocked,
              obbligatorio: oldParagraphs[index].obbligatorio,
            });
          });
          arrayToCheck.push({
            id: null,
            name: 'Nuovo capitolo',
            isTabOpen: false,
            numeroCapitolo: arrayToCheck.length + 1,
            paragraphData: paragraphToInsert,
          });
        } else {
          arrayToCheck.push({
            id: null,
            name: 'Nuovo capitolo',
            isTabOpen: false,
            numeroCapitolo: arrayToCheck.length + 1,
            paragraphData: [],
          });
        }
        this.setState(
          {
            tabListValues: arrayToCheck,
            chapterMode: true,
          },
          () => {
            this.updateParagrafiForChapterMode();
          },
        );
      }
    } else {
      let arrayToCheck = [...this.state.tabListValues];
      if (arrayToCheck.length === 0) {
        let oldParagraphs = [...this.state.tabs];
        let paragraphToInsert = [];
        oldParagraphs.forEach((_item, index) => {
          paragraphToInsert.push({
            label: oldParagraphs[index].label,
            nomeTab: oldParagraphs[index].label,
            isTabSelected:
              this.state.currentTabValue.value - 1 === index ? true : false,
            id: oldParagraphs[index].id,
            stato: oldParagraphs[index].stato,
            value: oldParagraphs[index].value,
            idManuale: oldParagraphs[index].idManuale,
            chapterIndex: 0,
            isSelected: true,
            isBlocked: oldParagraphs[index].isBlocked,
            obbligatorio: oldParagraphs[index].obbligatorio,
          });
        });
        arrayToCheck.push({
          id: null,
          name: 'Nuovo capitolo',
          isTabOpen: false,
          numeroCapitolo: arrayToCheck.length + 1,
          paragraphData: paragraphToInsert,
        });
      } else {
        arrayToCheck.push({
          id: null,
          name: 'Nuovo capitolo',
          isTabOpen: false,
          numeroCapitolo: arrayToCheck.length + 1,
          paragraphData: [],
        });
      }
      this.setState(
        {
          tabListValues: arrayToCheck,
          chapterMode: true,
        },
        () => {
          this.updateParagrafiForChapterMode();
        },
      );
    }
  };

  updateParagrafiForChapterMode = (index) => {
    const capitoli = [...this.state.tabListValues];
    let idManuale = this.props.manualeId;
    let capitoliToPass = [];
    capitoli.forEach((item) => {
      let objectToPushInCapitolo = {};
      let paragrafi = [];
      let capitolo = {
        id: item.id,
        manualeId: idManuale,
        numeroCapitolo: item.numeroCapitolo,
        titolo: item.name,
        stato: null,
      };
      item.paragraphData.forEach((item2) => {
        let paragrafo = {
          id: item2.id,
          manualeId: idManuale,
          capitoloId: item.id,
          numeroParagrafo: item2.value,
          titolo: item2.label,
          stato: item2.stato,
          obbligatorio: item2.obbligatorio,
          bloccato: item2.isBlocked,
          type: item2.tipo === 'Testo' ? null : item2.tipo,
          pdfId: item2.pdfId,
        };
        paragrafi.push(paragrafo);
      });
      objectToPushInCapitolo = {
        capitolo: capitolo,
        paragrafi: paragrafi,
      };
      capitoliToPass.push(objectToPushInCapitolo);
    });

    let bodyToSave = {
      manualeId: idManuale,
      capitoli: capitoliToPass,
    };

    putCapitoli(bodyToSave)
      .then((result) => {
        this.getParagrafiManualeInCostruzioneAfterSaveForChapters();
      })
      .catch((error) => {
        reportApiError(error);
      });
  };

  updateParagrafi = () => {
    const paragrafi = [...this.state.tabs];
    const paragrafiToPass = [];
    paragrafi.forEach((item) => {
      paragrafiToPass.push({
        bloccato: item.isBlocked,
        id: item.id,
        manualeId: item.idManuale,
        numeroParagrafo: item.value,
        obbligatorio: item.obbligatorio,
        stato: item.stato,
        titolo: item.label,
        type: item.tipo === 'Testo' ? null : item.tipo,
        pdfId: item.pdfId,
      });
    });

    let requestBody = {
      manualeId:
        paragrafi.length > 0
          ? paragrafi[0].idManuale
          : this.props.manualeId || this.state.idManuale,
      paragrafi: paragrafiToPass,
    };
    putModificaParagrafiManualePersonalizzato(requestBody)
      .then((result) => {
        this.getParagrafiManualeInCostruzione();
      })
      .catch((error) => {
        reportApiError(error);
        this.setState({ paragraphBlockState: false });
      });
  };

  abortPDFParagraphCreation = () => {
    this.setState({
      twoActionAlert: {
        isVisible: false,
        title: null,
        message: null,
        message2: null,
        indexForCapitolo: null,
        loading: false,
      },
      newParagraphFilePDF: null,
    });
  };

  onDocumentLoadSuccess = (number) => {
    this.setState({
      pdfNumberOfPages: number._pdfInfo.numPages,
      loading: false,
      paragraphBlockState: false,
    });
  };

  goToPreviousPDFPage = (number) => {
    let pageToReach = number - 1;
    if (pageToReach > 0) {
      this.setState({
        pdfLoadedPage: number - 1,
      });
    }
  };

  goToNextPDFPage = (number) => {
    let pageToReach = number + 1;
    if (pageToReach <= this.state.pdfNumberOfPages) {
      this.setState({
        pdfLoadedPage: number + 1,
      });
    }
  };

  createPDFParagraph = () => {
    this.setState((prevState) => ({
      twoActionAlert: {
        ...prevState.twoActionAlert,
        loading: true,
      },
    }));

    let formData = new FormData();
    formData.append('file', this.state.newParagraphFilePDF);
    postAggiuntaPDFForParagrafo(formData)
      .then((response) => {
        let arrayToCheck = [...this.state.tabs];
        let onlyNumbers = arrayToCheck.filter(
          (element) => typeof element.value === 'number',
        );

        if (onlyNumbers.length !== 0) {
          var numberOfParagraph = this.state.numberOfParagrafi;
          numberOfParagraph += 1;
        }
        let tabToPush = {
          id: null,
          idManuale: this.state.idManuale,
          isBlocked: false,
          isSelected: false,
          label: 'Nuovo PDF',
          obbligatorio: false,
          stato: 'BOZZA',
          value: null,
          tipo: 'PDF',
          pdfId: response.id,
        };
        arrayToCheck.push(tabToPush);
        this.setState(
          {
            tabs: arrayToCheck,
            numberOfParagrafi: numberOfParagraph,
          },
          () => {
            this.updateParagrafi();
          },
        );
        this.setState({
          twoActionAlert: {
            isVisible: false,
            title: null,
            message: null,
            message2: null,
            indexForCapitolo: null,
            loading: false,
          },
        });
      })
      .catch((error) => {
        this.setState((prevState) => ({
          twoActionAlert: {
            ...prevState.twoActionAlert,
            loading: false,
          },
        }));
        reportApiError(error);
      });
  };

  handleUpdateAPICallPDFInSottoparagrafo = (e) => {
    this.setState((prevState) => ({
      isTwoActionAlertForUpdateLoading: true,
    }));
    let paragraphDataToUpdate = null;
    let file = this.state.fileForUpdatePDF;
    let formData = new FormData();
    formData.append('file', file);
    postAggiuntaPDFForParagrafo(formData)
      .then((response) => {
        // Must be created here, once, and read from state in render() -
        // URL.createObjectURL() returns a new unique blob URL on every call,
        // even for the same File. render() passes this to react-pdf's
        // <Document file=...>, which reloads (and re-fetches the worker)
        // whenever `file` changes, so a value that changed on every render
        // would reload forever.
        if (this.state.modifiedPDFFileUrl) {
          URL.revokeObjectURL(this.state.modifiedPDFFileUrl);
        }
        const modifiedPDFFileUrl = URL.createObjectURL(file);
        if (this.state.chapterMode) {
          paragraphDataToUpdate = [...this.state.tabListValues];
          paragraphDataToUpdate[
            this.state.currentTabValue.chapterIndex
          ].paragraphData[this.state.currentTabValue.value - 1].pdfId =
            response.id;
          this.setState({
            modifiedPDFFile: file,
            modifiedPDFFileUrl,
            pdfLoadedPage: 1,
            //edit: true,
            isTwoActionAlertForUpdateVisible: false,
            tabListValues: paragraphDataToUpdate,
            isTwoActionAlertForUpdateLoading: false,
          });
        } else {
          paragraphDataToUpdate = [...this.state.tabs];
          paragraphDataToUpdate[this.state.currentTabValue.value - 1].pdfId =
            response.id;
          this.setState({
            modifiedPDFFile: file,
            modifiedPDFFileUrl,
            pdfLoadedPage: 1,
            //edit: true,
            tabs: paragraphDataToUpdate,
            isTwoActionAlertForUpdateVisible: false,
            isTwoActionAlertForUpdateLoading: false,
          });
        }
      })
      .catch((error) => {
        reportApiError(error);
      });
  };

  handleUpdatePDFInSottoparagrafo = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    if (
      file.type === 'application/pdf' &&
      Math.floor(file.size / 1000) <= 1000
    ) {
      this.setState({
        isTwoActionAlertForUpdateVisible: true,
        fileForUpdatePDF: file,
      });
    } else {
      if (Math.floor(file.size / 1000) > 10000) {
        reportError('Attenzione, il file ha una dimensione superiore ad 10MB');
      } else {
        reportError('Attenzione, il file inserito non è un PDF');
      }
    }
    // reset the input value so the user can upload the same file again
    event.target.value = '';
  };

  handlePDFLoading = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    if (
      file.type === 'application/pdf' &&
      Math.floor(file.size / 1000) <= 10000
    ) {
      this.setState({
        newParagraphFilePDF: file,
        twoActionAlert: {
          isVisible: true,
          indexForCapitolo: null,
          title: 'Conferma caricamento PDF',
          message:
            'Il documento inserito non sarà editabile e avrà una sua numerazione interna. La dimensione massima consentita è 10MB',
          message2:
            'Nome file: ' +
            file.name +
            ' Dimensione file:' +
            Math.floor(file.size / 1000) +
            ' kb',
          loading: false,
        },
      });
    } else {
      if (Math.floor(file.size / 1000) > 10000) {
        reportError('Attenzione, il file ha una dimensione superiore ad 10MB');
      } else {
        reportError('Attenzione, il file inserito non è un PDF');
      }
    }
    // reset the input value so the user can upload the same file again
    event.target.value = '';
  };

  handlePDFLoadingForCapitolo = (event, index) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    if (
      file.type === 'application/pdf' &&
      Math.floor(file.size / 1000) <= 10000
    ) {
      this.setState({
        newParagraphFilePDF: file,
        twoActionAlert: {
          isVisible: true,
          title: 'Conferma caricamento PDF',
          message:
            'Il documento inserito non sarà editabile e avrà una sua numerazione interna. La dimensione massima consentita è 10MB',
          message2:
            'Nome file: ' +
            file.name +
            ' Dimensione file:' +
            Math.floor(file.size / 1000) +
            ' kb',
          indexForCapitolo: index,
          loading: false,
        },
      });
    } else {
      if (Math.floor(file.size / 1000) > 10000) {
        reportError('Attenzione, il file ha una dimensione superiore ad 10MB');
      } else {
        reportError('Attenzione, il file inserito non è un PDF');
      }
    }
    // reset the input value so the user can upload the same file again
    event.target.value = '';
  };

  createPDFParagraphForCapitolo = () => {
    // The spinner belongs to the upload, so confirm before touching state:
    // every early return has to leave the dialog interactive.
    if (
      this.state.edit &&
      !window.confirm('Modifiche non salvate, continuare?')
    ) {
      return;
    }

    this.setState((prevState) => ({
      twoActionAlert: {
        ...prevState.twoActionAlert,
        loading: true,
      },
    }));

    let formData = new FormData();
    formData.append('file', this.state.newParagraphFilePDF);
    postAggiuntaPDFForParagrafo(formData)
      .then((response) => {
        let arrayOfChaptersToUpdate = [...this.state.tabListValues];
        let arrayToUpdate =
          arrayOfChaptersToUpdate[this.state.twoActionAlert.indexForCapitolo]
            .paragraphData;
        arrayToUpdate.push({
          id: null,
          idManuale: this.state.idManuale,
          isBlocked: false,
          isSelected: false,
          label: 'Nuovo PDF',
          obbligatorio: false,
          chapterIndex: this.state.twoActionAlert.indexForCapitolo,
          stato: 'BOZZA',
          value: null,
          nomeTab: 'Nuovo PDF',
          isTabSelected: false,
          tipo: 'PDF',
          pdfId: response.id,
        });
        this.setState(
          {
            tabListValues: arrayOfChaptersToUpdate,
            twoActionAlert: {
              isVisible: false,
              title: null,
              message: null,
              message2: null,
              indexForCapitolo: null,
              loading: false,
            },
          },
          () => {
            this.updateParagrafiForChapterMode(
              this.state.twoActionAlert.indexForCapitolo,
            );
          },
        );
      })
      .catch((error) => {
        this.setState((prevState) => ({
          twoActionAlert: {
            ...prevState.twoActionAlert,
            loading: false,
          },
        }));
        reportApiError(error);
      });
  };

  //Chapter operations here

  handleChapterOpening = (event, index) => {
    if (!Array.isArray(event._dispatchInstances)) {
      let array = [...this.state.tabListValues];
      array[index].isTabOpen = !array[index].isTabOpen;
      this.setState({
        tabListValues: array,
      });
    }
  };

  handleChapterElimination = (index) => {
    if (this.state.edit) {
      let confirmation = window.confirm('Modifiche non salvate, continuare?');
      if (confirmation) {
        let array = this.state.tabListValues;
        if (array[index].paragraphData.length === 0) {
          array.splice(index, 1);
          this.setState(
            {
              tabListValues: array,
            },
            () => {
              this.updateParagrafiForChapterMode();
            },
          );
        } else {
          alert(
            'Non è possibile eliminare un capitolo con paragrafi associati. Spostare i paragrafi in un altro capitolo e riprovare.',
          );
        }
      }
    } else {
      let array = this.state.tabListValues;
      if (array[index].paragraphData.length === 0) {
        array.splice(index, 1);
        this.setState(
          {
            tabListValues: array,
          },
          () => {
            this.updateParagrafiForChapterMode();
          },
        );
      } else {
        alert(
          'Non è possibile eliminare un capitolo con paragrafi associati. Spostare i paragrafi in un altro capitolo e riprovare.',
        );
      }
    }
  };

  handleChapterMovementUpward = (index) => {
    if (this.state.edit) {
      let confirmation = window.confirm('Modifiche non salvate, continuare?');
      if (confirmation) {
        if (index > 0) {
          let array = [...this.state.tabListValues];
          swapChapterElements(array, index, index - 1);
          this.setState(
            {
              tabListValues: array,
            },
            () => {
              this.updateParagrafiForChapterMode();
            },
          );
        }
      }
    } else {
      if (index > 0) {
        let array = [...this.state.tabListValues];
        swapChapterElements(array, index, index - 1);
        this.setState(
          {
            tabListValues: array,
          },
          () => {
            this.updateParagrafiForChapterMode();
          },
        );
      }
    }
  };

  handleChapterMovementDownward = (index) => {
    if (this.state.edit) {
      let confirmation = window.confirm('Modifiche non salvate, continuare?');
      if (confirmation) {
        let array = [...this.state.tabListValues];
        if (index < array.length - 1) {
          swapChapterElements(array, index, index + 1);
          this.setState(
            {
              tabListValues: array,
            },
            () => {
              this.updateParagrafiForChapterMode();
            },
          );
        }
      }
    } else {
      let array = [...this.state.tabListValues];
      if (index < array.length - 1) {
        swapChapterElements(array, index, index + 1);
        this.setState(
          {
            tabListValues: array,
          },
          () => {
            this.updateParagrafiForChapterMode();
          },
        );
      }
    }
  };

  //End operations here

  //Tab paragraphs under chapters operations here

  handleChangeTabWithChapters = (event, value, index) => {
    if (!this.state.paragraphBlockState) {
      if (!Array.isArray(event._dispatchInstances)) {
        if (this.state.edit) {
          let confirmation = window.confirm(
            'Modifiche non salvate, cambiare paragrafo?',
          );
          if (confirmation) {
            this.handleTabChangeNew(value, index);
          }
        } else {
          this.handleTabChangeNew(value, index);
        }
      }
    }
  };

  toggleParagraphLockChapters = (item, index) => {
    if (this.state.edit) {
      let confirmation = window.confirm('Modifiche non salvate, continuare?');
      if (confirmation) {
        let objectToChange = [...this.state.tabListValues];
        objectToChange[index].paragraphData.find((e) => {
          if (e.value === item.value) {
            e.isBlocked = !e.isBlocked;
          }
        });
        this.setState(
          {
            tabListValues: objectToChange,
          },
          () => {
            this.updateParagrafiForChapterMode();
          },
        );
      }
    } else {
      let objectToChange = [...this.state.tabListValues];
      objectToChange[index].paragraphData.find((e) => {
        if (e.value === item.value) {
          e.isBlocked = !e.isBlocked;
        }
      });
      this.setState(
        {
          tabListValues: objectToChange,
        },
        () => {
          this.updateParagrafiForChapterMode();
        },
      );
    }
  };

  handleDeleteParagraphForChapters = (index, value) => {
    if (this.state.paragraphBlockState) return;
    const updatedTabListValues = this.state.tabListValues.map(
      (tab, chapterIndex) =>
        chapterIndex === index
          ? {
              ...tab,
              paragraphData: tab.paragraphData.filter(
                (_, paragraphIndex) => paragraphIndex !== value,
              ),
            }
          : tab,
    );

    this.setState(
      {
        tabListValues: updatedTabListValues,
        paragraphBlockState: true,
      },
      () => {
        this.updateParagrafiForChapterMode();
      },
    );
  };

  handleDeleteParagraphFromMainPanelButton = () => {
    if (!this.state.paragraphBlockState) {
      let newArrayValue = this.state.tabValue;
      let objectToErase = this.state.currentTabValue;
      let newArray = this.state.tabs.filter(
        (item) => item.value !== objectToErase.value,
      );
      if (
        !this.state.currentTabValue.isBlocked ||
        localStorage.getItem('superconsulente') === 'true' ||
        localStorage.getItem('consulenteId') === 'null'
      ) {
        if (!this.state.chapterMode) {
          if (newArrayValue === objectToErase.value - 1) {
            setChapterIndex(0);
            setParagraphIndex(0);
          }
          if (newArrayValue.obbligatorio !== true) {
            this.setState(
              {
                tabs: newArray,
                numberOfParagrafi: this.state.numberOfParagrafi - 1,
                paragraphBlockState: true,
              },
              function () {
                this.updateParagrafi();
              },
            );
          } else if (!this.state.currentTabValue.isBlocked) {
            if (newArrayValue.obbligatorio !== true) {
              this.setState(
                {
                  tabs: newArray,
                  numberOfParagrafi: this.state.numberOfParagrafi - 1,
                  paragraphBlockState: true,
                },
                function () {
                  this.updateParagrafi();
                },
              );
            }
          } else {
            alert(
              'Attenzione, operazione di cancellazione permessa solo ai superconsulenti!',
            );
          }
        } else {
          let array = this.state.tabListValues;
          let currentTabValue = this.state.currentTabValue;
          array[currentTabValue.chapterIndex].paragraphData.splice(
            currentTabValue.value - 1,
            1,
          );
          this.setState(
            {
              tabListValues: array,
              paragraphBlockState: true,
            },
            () => {
              this.updateParagrafiForChapterMode();
            },
          );
        }
      } else {
        alert(
          'Attenzione, operazione di cancellazione permessa solo ai superconsulenti!',
        );
      }
    }
  };

  handleParagraphMovementUpwardForChapters = (item, index, value) => {
    if (this.state.edit) {
      let confirmation = window.confirm('Modifiche non salvate, continuare?');
      if (confirmation) {
        let arrayOfChapters = [...this.state.tabListValues];
        let arrayToUpdate = arrayOfChapters[index].paragraphData;
        if (value > 0) {
          swapTabElements(arrayToUpdate, value, value - 1);
          this.setState(
            {
              tabListValues: arrayOfChapters,
            },
            () => {
              this.updateParagrafiForChapterMode();
            },
          );
        } else if (index > 0) {
          arrayToUpdate.splice(value, 1);
          arrayOfChapters[index - 1].paragraphData.push(item);
          arrayOfChapters[index - 1].paragraphData[
            arrayOfChapters[index - 1].paragraphData.length - 1
          ].chapterIndex = index - 1;
          arrayOfChapters[index - 1].paragraphData.forEach((item, index) => {
            item.value = index + 1;
          });
          arrayOfChapters[index].paragraphData.forEach((item, index) => {
            item.value = index + 1;
          });
          this.setState(
            {
              tabListValues: arrayOfChapters,
            },
            () => {
              this.updateParagrafiForChapterMode();
            },
          );
        }
      }
    } else {
      let arrayOfChapters = [...this.state.tabListValues];
      let arrayToUpdate = arrayOfChapters[index].paragraphData;
      if (value > 0) {
        swapTabElements(arrayToUpdate, value, value - 1);
        this.setState(
          {
            tabListValues: arrayOfChapters,
          },
          () => {
            this.updateParagrafiForChapterMode();
          },
        );
      } else if (index > 0) {
        arrayToUpdate.splice(value, 1);
        arrayOfChapters[index - 1].paragraphData.push(item);
        arrayOfChapters[index - 1].paragraphData[
          arrayOfChapters[index - 1].paragraphData.length - 1
        ].chapterIndex = index - 1;
        arrayOfChapters[index - 1].paragraphData.forEach((item, index) => {
          item.value = index + 1;
        });
        arrayOfChapters[index].paragraphData.forEach((item, index) => {
          item.value = index + 1;
        });
        this.setState(
          {
            tabListValues: arrayOfChapters,
          },
          () => {
            this.updateParagrafiForChapterMode();
          },
        );
      }
    }
  };

  handleParagraphMovementDownwardForChapters = (item, index, value) => {
    if (this.state.edit) {
      let confirmation = window.confirm('Modifiche non salvate, continuare?');
      if (confirmation) {
        let arrayOfChapters = [...this.state.tabListValues];
        let arrayToUpdate = arrayOfChapters[index].paragraphData;
        if (value < arrayToUpdate.length - 1) {
          swapTabElements(arrayToUpdate, value, value + 1);
          this.setState(
            {
              tabListValues: arrayOfChapters,
            },
            () => {
              this.updateParagrafiForChapterMode();
            },
          );
        } else if (index < arrayOfChapters.length - 1) {
          arrayToUpdate.splice(value, 1);
          arrayOfChapters[index + 1].paragraphData.unshift(item);
          arrayOfChapters[index + 1].paragraphData[0].chapterIndex = index + 1;
          arrayOfChapters[index + 1].paragraphData.forEach((item, index) => {
            item.value = index + 1;
          });
          arrayOfChapters[index].paragraphData.forEach((item, index) => {
            item.value = index + 1;
          });
          this.setState(
            {
              tabListValues: arrayOfChapters,
            },
            () => {
              this.updateParagrafiForChapterMode();
            },
          );
        }
      }
    } else {
      let arrayOfChapters = [...this.state.tabListValues];
      let arrayToUpdate = arrayOfChapters[index].paragraphData;
      if (value < arrayToUpdate.length - 1) {
        swapTabElements(arrayToUpdate, value, value + 1);
        this.setState(
          {
            tabListValues: arrayOfChapters,
          },
          () => {
            this.updateParagrafiForChapterMode();
          },
        );
      } else if (index < arrayOfChapters.length - 1) {
        arrayToUpdate.splice(value, 1);
        arrayOfChapters[index + 1].paragraphData.unshift(item);
        arrayOfChapters[index + 1].paragraphData[0].chapterIndex = index + 1;
        arrayOfChapters[index + 1].paragraphData.forEach((item, index) => {
          item.value = index + 1;
        });
        arrayOfChapters[index].paragraphData.forEach((item, index) => {
          item.value = index + 1;
        });
        this.setState(
          {
            tabListValues: arrayOfChapters,
          },
          () => {
            this.updateParagrafiForChapterMode();
          },
        );
      }
    }
  };

  openAddParagraphMenu = (event, chapterIndex) => {
    event.stopPropagation();
    this.setState({
      addParagraphMenu: {
        anchorEl: event.currentTarget,
        chapterIndex: chapterIndex != null ? chapterIndex : null,
      },
    });
  };

  closeAddParagraphMenu = () => {
    this.setState((prevState) => ({
      addParagraphMenu: {
        ...prevState.addParagraphMenu,
        anchorEl: null,
      },
    }));
  };

  handleAddParagraphFromMenu = (event) => {
    const { chapterIndex } = this.state.addParagraphMenu;
    this.closeAddParagraphMenu();
    if (chapterIndex != null) {
      this.handleParagraphCreationForChapters(event, chapterIndex);
    } else {
      this.handleParagraphCreation();
    }
  };

  handleUploadPDFFromMenu = (event) => {
    const { chapterIndex } = this.state.addParagraphMenu;
    this.closeAddParagraphMenu();
    if (chapterIndex != null) {
      this.handlePDFLoadingForCapitolo(event, chapterIndex);
    } else {
      this.handlePDFLoading(event);
    }
  };

  handleParagraphCreationForChapters = (event, index) => {
    if (!this.state.paragraphBlockState) {
      if (this.state.edit) {
        let confirmation = window.confirm('Modifiche non salvate, continuare?');
        if (confirmation) {
          let arrayOfChaptersToUpdate = [...this.state.tabListValues];
          let arrayToUpdate = arrayOfChaptersToUpdate[index].paragraphData;
          arrayToUpdate.push({
            id: null,
            idManuale: this.state.idManuale,
            isBlocked: false,
            isSelected: false,
            label: 'Nuovo paragrafo',
            obbligatorio: false,
            chapterIndex: index,
            stato: 'BOZZA',
            value: null,
            nomeTab: 'Nuovo paragrafo',
            isTabSelected: false,
          });
          this.setState(
            {
              tabListValues: arrayOfChaptersToUpdate,
              paragraphBlockState: true,
            },
            () => {
              this.updateParagrafiForChapterMode(index);
            },
          );
        }
      } else {
        let arrayOfChaptersToUpdate = [...this.state.tabListValues];
        let arrayToUpdate = arrayOfChaptersToUpdate[index].paragraphData;
        arrayToUpdate.push({
          id: null,
          idManuale: this.state.idManuale,
          isBlocked: false,
          isSelected: false,
          label: 'Nuovo paragrafo',
          obbligatorio: false,
          chapterIndex: index,
          stato: 'BOZZA',
          value: null,
          nomeTab: 'Nuovo paragrafo',
          isTabSelected: false,
        });
        this.setState(
          {
            tabListValues: arrayOfChaptersToUpdate,
            paragraphBlockState: true,
          },
          () => {
            this.updateParagrafiForChapterMode(index);
          },
        );
      }
    }
  };

  //End tab paragraph operations region

  //No chapter mode paragraph operations here

  handleParagraphCreation = () => {
    if (!this.state.paragraphBlockState) {
      let arrayToCheck = [...this.state.tabs];
      let onlyNumbers = arrayToCheck.filter(
        (element) => typeof element.value === 'number',
      );

      if (onlyNumbers.length !== 0) {
        var numberOfParagraph = this.state.numberOfParagrafi;
        numberOfParagraph += 1;
      }
      let tabToPush = {
        id: null,
        idManuale: this.state.idManuale,
        isBlocked: false,
        isSelected: false,
        label: 'Nuovo paragrafo',
        obbligatorio: false,
        stato: 'BOZZA',
        value: null,
      };
      arrayToCheck.push(tabToPush);
      this.setState(
        {
          tabs: arrayToCheck,
          numberOfParagrafi: numberOfParagraph,
          paragraphBlockState: true,
        },
        () => {
          this.updateParagrafi();
        },
      );
    }
  };

  //End No chapter mode operations region

  getSottoparagrafi = (id) => {
    this.setState({ sottoparagrafiLoading: true });
    getContenutoParagrafo(id)
      .then((result) => {
        this.setState({
          sottoParagrafiData: result.componenti,
          isTabChangePressed: false,
        });
      })
      .then((result) => {
        this.loadSottoparagrafi();
      })
      .catch((error) => {
        this.setState({ sottoparagrafiLoading: false });
        reportApiError(error);
      });
  };

  loadSottoparagrafi = () => {
    let sottoParagrafiArray = [];
    let numberToIncrease = this.state.numberOfSottoparagrafo;
    let sottoParagrafiData = [...this.state.sottoParagrafiData];
    sottoParagrafiData.forEach((item) => {
      if (item.componenti[0] === undefined) {
        if (item.tipoContenuto === 'TABELLA_REPARTI') {
          sottoParagrafiArray.push({
            index: item.numeroComponente,
            value: (
              <CustomTableForManuale
                theme={this.props.theme}
                key={item.numeroComponente}
                aggiornaReparti={this.aggiornaReparti}
                aggiornaRepartiForResponsabili={
                  this.aggiornaRepartiForResponsabili
                }
                isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                mode={'Definizione_Reparto'}
                kind={this.props.mode}
                firstElement={true}
                titoloInizialeSottoparagrafo={item.titolo}
                idManuale={this.state.idManuale}
                modalTitle={item.titolo}
                numberOfRows={this.state.numberOfLines}
              />
            ),
          });
        } else if (item.tipoContenuto === 'TABELLA_LOCALI') {
          sottoParagrafiArray.push({
            index: item.numeroComponente,
            value: (
              <CustomTableForManuale
                theme={this.props.theme}
                key={item.numeroComponente}
                mode={'Descrizione_Reparto'}
                aggiornaLocali={this.aggiornaLocali}
                isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                firstElement={true}
                titoloInizialeSottoparagrafo={item.titolo}
                idManuale={this.state.idManuale}
                modalTitle={item.titolo}
                numberOfRows={this.state.numberOfLines}
              />
            ),
          });
        } else if (item.tipoContenuto === 'TABELLA_PROCEDURE') {
          sottoParagrafiArray.push({
            index: item.numeroComponente,
            value: (
              <CustomTableForManuale
                theme={this.props.theme}
                key={item.numeroComponente}
                idParagrafo={item.paragrafoId}
                isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                indexTab={item.numeroComponente}
                mode={'Definizione_Procedure_Autocontrollo'}
                bringDataToParent={this.bringDataToParentAutocontrollo}
                bringDataAfterExchange={
                  this.bringDataToParentAutocontrolloAfterExchange
                }
                bringTextToParent={this.bringTextToParentAutocontrollo}
                bringTitleToParent={this.bringTitleToParent}
                sottoParagrafiData={this.state.sottoParagrafiData}
                firstElement={true}
                itemScheda={item}
                titoloInizialeSottoparagrafo={item.titolo}
                idManuale={this.state.idManuale}
                modalTitle={item.titolo}
                numberOfRows={this.state.numberOfLines}
              />
            ),
          });
        }
      } else if (item.tipoContenuto === 'TABELLA_PROCEDURE') {
        sottoParagrafiArray.push({
          index: item.numeroComponente,
          value: (
            <CustomTableForManuale
              theme={this.props.theme}
              key={item.numeroComponente}
              indexTab={item.numeroComponente}
              isParagrafoBlocked={this.state.currentTabValue.isBlocked}
              bringTitleToParent={this.bringTitleToParent}
              idParagrafo={item.paragrafoId}
              mode={'Definizione_Procedure_Autocontrollo'}
              bringDataAfterExchange={
                this.bringDataToParentAutocontrolloAfterExchange
              }
              bringDataToParent={this.bringDataToParentAutocontrollo}
              bringTextToParent={this.bringTextToParentAutocontrollo}
              sottoParagrafiData={this.state.sottoParagrafiData}
              firstElement={true}
              itemScheda={item}
              rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
              titoloInizialeSottoparagrafo={item.titolo}
              idManuale={this.state.idManuale}
              modalTitle={item.titolo}
              numberOfRows={this.state.numberOfLines}
            />
          ),
        });
      } else {
        if (item.componenti[0].tipo === 'BLOCCO_TESTO') {
          if (item.numeroComponente === 0) {
            if (item.titolo === null) {
              sottoParagrafiArray.push({
                index: item.numeroComponente,
                value: (
                  <NewSottoparagrafoTestoEImmagine
                    theme={this.props.theme}
                    key={item.numeroComponente}
                    handleEditChange={this.handleEditChange}
                    isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                    sottoParagrafiData={this.state.sottoParagrafiData}
                    bringDataToParent={this.bringDataToParent}
                    deleteImage={this.deleteImageFromSottoparagrafo}
                    immaginiParagrafo={item.immagini}
                    bringTitleToParent={this.bringTitleToParent}
                    testoSottoParagrafo={item.componenti[0].contenuto}
                    indexValue={item.numeroComponente}
                    firstElement={true}
                    titoloInizialeSottoparagrafo={
                      item.componenti[0].titolo === null
                        ? ''
                        : item.componenti[0].titolo
                    }
                    rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
                    idImmagine={item.numeroComponente}
                  />
                ),
              });
            } else {
              sottoParagrafiArray.push({
                index: item.numeroComponente,
                value: (
                  <NewSottoparagrafoTestoEImmagine
                    theme={this.props.theme}
                    key={item.numeroComponente}
                    handleEditChange={this.handleEditChange}
                    isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                    sottoParagrafiData={this.state.sottoParagrafiData}
                    bringDataToParent={this.bringDataToParent}
                    deleteImage={this.deleteImageFromSottoparagrafo}
                    immaginiParagrafo={item.immagini}
                    bringTitleToParent={this.bringTitleToParent}
                    testoSottoParagrafo={item.componenti[0].contenuto}
                    indexValue={item.numeroComponente}
                    firstElement={true}
                    titoloInizialeSottoparagrafo={
                      item.componenti[0].titolo === null
                        ? 'Nuovo Sottoparagrafo'
                        : item.componenti[0].titolo
                    }
                    rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
                    idImmagine={item.numeroComponente}
                  />
                ),
              });
            }
          } else {
            sottoParagrafiArray.push({
              index: item.numeroComponente,
              value: (
                <NewSottoparagrafoTestoEImmagine
                  theme={this.props.theme}
                  key={item.numeroComponente}
                  handleEditChange={this.handleEditChange}
                  isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                  sottoParagrafiData={this.state.sottoParagrafiData}
                  moveUpward={this.handleSottoparagrafoMovementUpward}
                  moveDownward={this.handleSottoparagrafoMovementDownward}
                  indexValue={item.numeroComponente}
                  deleteImage={this.deleteImageFromSottoparagrafo}
                  immaginiParagrafo={item.immagini}
                  bringDataToParent={this.bringDataToParent}
                  bringTitleToParent={this.bringTitleToParent}
                  testoSottoParagrafo={item.componenti[0].contenuto}
                  firstElement={false}
                  titoloInizialeSottoparagrafo={
                    item.titolo === null ? 'Nuovo Sottoparagrafo' : item.titolo
                  }
                  rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
                  idImmagine={item.numeroComponente}
                />
              ),
            });
          }
        }
        if (item.componenti[0].tipo === 'TABELLA') {
          if (item.componenti[0].organigramma) {
            sottoParagrafiArray.push({
              index: item.numeroComponente,
              value: (
                <CustomTableForManuale
                  theme={this.props.theme}
                  key={item.numeroComponente}
                  isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                  firstElement={true}
                  sottoParagrafiData={this.state.sottoParagrafiData}
                  mode={'Organigramma'}
                  changeEditMode={this.handleEditChange}
                  bringDataToParentAutocontrolloOrganigramma={
                    this.bringDataToParentAutocontrolloOrganigramma
                  }
                  kind={this.props.mode}
                  idParagrafo={item.paragrafoId}
                  idManuale={this.state.idManuale}
                  modalTitle={item.titolo}
                  titoloInizialeSottoparagrafo={item.titolo}
                  indexTab={item.numeroComponente}
                  numberOfRows={this.state.numberOfLines}
                />
              ),
            });
          } else {
            sottoParagrafiArray.push({
              index: item.numeroComponente,
              value: (
                <CustomTableForManuale
                  theme={this.props.theme}
                  key={item.numeroComponente}
                  isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                  mode={'Custom'}
                  updateCustomTable={this.aggiornaTabellaCustomHandler}
                  moveUpward={this.handleSottoparagrafoMovementUpward}
                  moveDownward={this.handleSottoparagrafoMovementDownward}
                  titoloParagrafo={item.titolo}
                  sottoParagrafiData={this.state.sottoParagrafiData}
                  idParagrafo={item.paragrafoId}
                  modalTitle={item.titolo}
                  titoloInizialeSottoparagrafo={item.titolo}
                  bringTitleToParent={this.bringTitleToParent}
                  idManuale={this.state.idManuale}
                  indexTab={this.state.sottoParagrafoIndex}
                  handleRimuoviTabella={this.handleRimuoviTabella}
                  rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
                  columns={this.state.colonneTabellaCustom}
                  numberOfRows={this.state.numberOfLines}
                />
              ),
            });
          }
        }
        if (item.componenti[0].tipo === 'TABELLA_PERSONALIZZATA') {
          let contenuto = item.componenti[0].contenuto;
          let colonne = item.componenti[0].definizioniColonne;
          var array = JSON.parse('[' + colonne + ']');
          this.setState({
            colonneTabellaCustom: array[0],
          });
          let columnsToShow = [];
          array[0].forEach((item, index) => {
            columnsToShow.push({
              id: 'Colonna' + index,
              header: item,
            });
          });
          sottoParagrafiArray.push({
            index: item.numeroComponente,
            value: (
              <CustomTableForManuale
                theme={this.props.theme}
                key={item.numeroComponente}
                isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                sottoParagrafiData={this.state.sottoParagrafiData}
                mode={'Custom'}
                updateCustomTable={this.aggiornaTabellaCustomHandler}
                moveUpward={this.handleSottoparagrafoMovementUpward}
                moveDownward={this.handleSottoparagrafoMovementDownward}
                modalTitle={item.titolo}
                titoloParagrafo={item.titolo}
                titoloInizialeSottoparagrafo={item.titolo}
                bringTitleToParent={this.bringTitleToParent}
                contenuto={contenuto}
                idManuale={this.state.idManuale}
                idParagrafo={item.paragrafoId}
                indexTab={item.numeroComponente}
                handleRimuoviTabella={this.handleRimuoviTabella}
                rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
                columns={columnsToShow}
                numberOfRows={this.state.numberOfLines}
              />
            ),
          });
        }
      }
    });
    numberToIncrease = sottoParagrafiArray.length;
    this.setState({
      sottoParagrafi: sottoParagrafiArray,
      numberOfSottoparagrafo: numberToIncrease,
      sottoParagrafoIndex: numberToIncrease,
      isSaveButtonPressed: false,
      colonneTabellaCustom: [],
      paragraphBlockState: false,
      sottoparagrafiLoading: false,
    });
  };

  handleEditChange = () => {
    this.setState({
      edit: true,
    });
  };

  loadSottoparagrafiAfterSwap = () => {
    let sottoParagrafiArray = [];
    let numberToIncrease = this.state.numberOfSottoparagrafo;
    let sottoParagrafiData = [...this.state.sottoParagrafiData];
    sottoParagrafiData.forEach((item, index) => {
      if (item !== undefined) {
        if (item.componenti[0] === undefined) {
          if (item.tipoContenuto === 'TABELLA_REPARTI') {
            sottoParagrafiArray.push({
              index: index,
              value: (
                <CustomTableForManuale
                  theme={this.props.theme}
                  aggiornaReparti={this.aggiornaReparti}
                  aggiornaRepartiForResponsabili={
                    this.aggiornaRepartiForResponsabili
                  }
                  isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                  mode={'Definizione_Reparto'}
                  kind={this.props.mode}
                  firstElement={true}
                  titoloInizialeSottoparagrafo={item.titolo}
                  idManuale={this.state.idManuale}
                  modalTitle={item.titolo}
                  numberOfRows={this.state.numberOfLines}
                />
              ),
            });
          } else if (item.tipoContenuto === 'TABELLA_LOCALI') {
            sottoParagrafiArray.push({
              index: index,
              value: (
                <CustomTableForManuale
                  theme={this.props.theme}
                  aggiornaLocali={this.aggiornaLocali}
                  isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                  mode={'Descrizione_Reparto'}
                  firstElement={true}
                  titoloInizialeSottoparagrafo={item.titolo}
                  idManuale={this.state.idManuale}
                  modalTitle={item.titolo}
                  numberOfRows={this.state.numberOfLines}
                />
              ),
            });
          } else if (item.tipoContenuto === 'TABELLA_PROCEDURE') {
            sottoParagrafiArray.push({
              index: index,
              value: (
                <CustomTableForManuale
                  theme={this.props.theme}
                  isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                  idParagrafo={item.paragrafoId}
                  indexTab={index}
                  mode={'Definizione_Procedure_Autocontrollo'}
                  bringDataToParent={this.bringDataToParentAutocontrollo}
                  bringDataAfterExchange={
                    this.bringDataToParentAutocontrolloAfterExchange
                  }
                  bringTextToParent={this.bringTextToParentAutocontrollo}
                  bringTitleToParent={this.bringTitleToParent}
                  sottoParagrafiData={this.state.sottoParagrafiData}
                  firstElement={true}
                  itemScheda={item}
                  titoloInizialeSottoparagrafo={item.titolo}
                  idManuale={this.state.idManuale}
                  modalTitle={item.titolo}
                  numberOfRows={this.state.numberOfLines}
                />
              ),
            });
          }
        } else if (item.tipoContenuto === 'TABELLA_PROCEDURE') {
          sottoParagrafiArray.push({
            index: index,
            value: (
              <CustomTableForManuale
                theme={this.props.theme}
                isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                indexTab={index}
                bringTitleToParent={this.bringTitleToParent}
                idParagrafo={item.paragrafoId}
                mode={'Definizione_Procedure_Autocontrollo'}
                bringDataAfterExchange={
                  this.bringDataToParentAutocontrolloAfterExchange
                }
                bringDataToParent={this.bringDataToParentAutocontrollo}
                bringTextToParent={this.bringTextToParentAutocontrollo}
                sottoParagrafiData={this.state.sottoParagrafiData}
                firstElement={true}
                itemScheda={item}
                rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
                titoloInizialeSottoparagrafo={item.titolo}
                idManuale={this.state.idManuale}
                modalTitle={item.titolo}
                numberOfRows={this.state.numberOfLines}
              />
            ),
          });
        } else {
          if (item.componenti[0].tipo === 'BLOCCO_TESTO') {
            if (index === 0) {
              if (item.titolo === null) {
                sottoParagrafiArray.push({
                  index: index,
                  value: (
                    <NewSottoparagrafoTestoEImmagine
                      theme={this.props.theme}
                      isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                      sottoParagrafiData={this.state.sottoParagrafiData}
                      bringDataToParent={this.bringDataToParent}
                      deleteImage={this.deleteImageFromSottoparagrafo}
                      handleEditChange={this.handleEditChange}
                      immaginiParagrafo={item.immagini}
                      bringTitleToParent={this.bringTitleToParent}
                      testoSottoParagrafo={item.componenti[0].contenuto}
                      indexValue={index}
                      firstElement={true}
                      titoloInizialeSottoparagrafo={
                        item.componenti[0].titolo === null
                          ? ''
                          : item.componenti[0].titolo
                      }
                      rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
                      idImmagine={index}
                    />
                  ),
                });
              } else {
                sottoParagrafiArray.push({
                  index: index,
                  value: (
                    <NewSottoparagrafoTestoEImmagine
                      theme={this.props.theme}
                      handleEditChange={this.handleEditChange}
                      isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                      sottoParagrafiData={this.state.sottoParagrafiData}
                      bringDataToParent={this.bringDataToParent}
                      deleteImage={this.deleteImageFromSottoparagrafo}
                      immaginiParagrafo={item.immagini}
                      bringTitleToParent={this.bringTitleToParent}
                      testoSottoParagrafo={item.componenti[0].contenuto}
                      indexValue={index}
                      firstElement={true}
                      titoloInizialeSottoparagrafo={
                        item.componenti[0].titolo === null
                          ? 'Nuovo Sottoparagrafo'
                          : item.componenti[0].titolo
                      }
                      rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
                      idImmagine={index}
                    />
                  ),
                });
              }
            } else {
              sottoParagrafiArray.push({
                index: index,
                value: (
                  <NewSottoparagrafoTestoEImmagine
                    theme={this.props.theme}
                    handleEditChange={this.handleEditChange}
                    isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                    sottoParagrafiData={this.state.sottoParagrafiData}
                    moveUpward={this.handleSottoparagrafoMovementUpward}
                    moveDownward={this.handleSottoparagrafoMovementDownward}
                    indexValue={index}
                    deleteImage={this.deleteImageFromSottoparagrafo}
                    immaginiParagrafo={item.immagini}
                    bringDataToParent={this.bringDataToParent}
                    bringTitleToParent={this.bringTitleToParent}
                    testoSottoParagrafo={item.componenti[0].contenuto}
                    firstElement={false}
                    titoloInizialeSottoparagrafo={
                      item.titolo === null
                        ? 'Nuovo Sottoparagrafo'
                        : item.titolo
                    }
                    rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
                    idImmagine={index}
                  />
                ),
              });
            }
          }
          if (item.componenti[0].tipo === 'TABELLA') {
            if (item.componenti[0].organigramma) {
              sottoParagrafiArray.push({
                index: index,
                value: (
                  <CustomTableForManuale
                    theme={this.props.theme}
                    isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                    firstElement={true}
                    sottoParagrafiData={this.state.sottoParagrafiData}
                    mode={'Organigramma'}
                    changeEditMode={this.handleEditChange}
                    kind={this.props.mode}
                    idParagrafo={item.paragrafoId}
                    bringDataToParentAutocontrolloOrganigramma={
                      this.bringDataToParentAutocontrolloOrganigramma
                    }
                    indexTab={index}
                    idManuale={this.state.idManuale}
                    modalTitle={item.titolo}
                    titoloInizialeSottoparagrafo={item.titolo}
                    numberOfRows={this.state.numberOfLines}
                  />
                ),
              });
            } else {
              sottoParagrafiArray.push({
                index: index,
                value: (
                  <CustomTableForManuale
                    theme={this.props.theme}
                    isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                    mode={'Custom'}
                    updateCustomTable={this.aggiornaTabellaCustomHandler}
                    moveUpward={this.handleSottoparagrafoMovementUpward}
                    moveDownward={this.handleSottoparagrafoMovementDownward}
                    titoloParagrafo={item.titolo}
                    sottoParagrafiData={this.state.sottoParagrafiData}
                    idParagrafo={item.paragrafoId}
                    modalTitle={item.titolo}
                    titoloInizialeSottoparagrafo={item.titolo}
                    bringTitleToParent={this.bringTitleToParent}
                    idManuale={this.state.idManuale}
                    indexTab={index}
                    handleRimuoviTabella={this.handleRimuoviTabella}
                    rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
                    columns={this.state.colonneTabellaCustom}
                    numberOfRows={this.state.numberOfLines}
                  />
                ),
              });
            }
          }
          if (item.componenti[0].tipo === 'TABELLA_PERSONALIZZATA') {
            let contenuto = item.componenti[0].contenuto;
            let colonne = item.componenti[0].definizioniColonne;
            var array = JSON.parse('[' + colonne + ']');
            this.setState({
              colonneTabellaCustom: array[0],
            });
            let columnsToShow = [];
            array[0].forEach((item, index) => {
              columnsToShow.push({
                id: 'Colonna' + index,
                header: item,
              });
            });
            sottoParagrafiArray.push({
              index: index,
              value: (
                <CustomTableForManuale
                  theme={this.props.theme}
                  isParagrafoBlocked={this.state.currentTabValue.isBlocked}
                  sottoParagrafiData={this.state.sottoParagrafiData}
                  mode={'Custom'}
                  updateCustomTable={this.aggiornaTabellaCustomHandler}
                  moveUpward={this.handleSottoparagrafoMovementUpward}
                  moveDownward={this.handleSottoparagrafoMovementDownward}
                  modalTitle={item.titolo}
                  titoloParagrafo={item.titolo}
                  titoloInizialeSottoparagrafo={item.titolo}
                  bringTitleToParent={this.bringTitleToParent}
                  contenuto={contenuto}
                  idManuale={this.state.idManuale}
                  idParagrafo={item.paragrafoId}
                  indexTab={index}
                  handleRimuoviTabella={this.handleRimuoviTabella}
                  rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
                  columns={columnsToShow}
                  numberOfRows={this.state.numberOfLines}
                />
              ),
            });
          }
        }
        item.numeroComponente = index;
      }
    });
    numberToIncrease = sottoParagrafiArray.length;
    this.setState({
      sottoParagrafi: [...sottoParagrafiArray],
      numberOfSottoparagrafo: numberToIncrease,
      sottoParagrafoIndex: numberToIncrease,
      sottoParagrafiData: sottoParagrafiData,
      colonneTabellaCustom: [],
      sottoparagrafiLoading: false,
    });
  };

  handleTabChange = (event, newValue) => {
    this.setState({
      isTabChangePressed: true,
    });
    if (
      !Array.isArray(event._dispatchInstances) &&
      !this.state.paragraphBlockState
    ) {
      setParagraphIndex(newValue);
      this.setState({
        loading: true,
        loadingPDF: true,
        editParagraphTitle: false,
      });
      if (this.state.edit === false) {
        this.setState({
          tabValue: newValue,
        });
        this.setState({
          showTables: false,
          sottoParagrafi: [],
          tabelle: [],
          modifiedPDFFile: null,
          modifiedPDFFileUrl: null,
          pdfLoadedPage: 1,
        });

        let paragrafoElement = this.state.tabs.at(newValue);
        if (
          paragrafoElement.obbligatorio &&
          paragrafoElement.label === 'Organigramma'
        ) {
          this.getSottoparagrafi(paragrafoElement.id);
          this.setState({
            modalTitle: 'Organigramma',
            currentTabValue: paragrafoElement,
          });
        } else if (
          paragrafoElement.obbligatorio &&
          paragrafoElement.label === 'Definizione dei reparti'
        ) {
          this.getSottoparagrafi(paragrafoElement.id);
          this.setState({
            modalTitle: 'Definizione Reparti',
            currentTabValue: paragrafoElement,
          });
        } else if (
          paragrafoElement.obbligatorio &&
          paragrafoElement.label === 'Descrizione dei reparti'
        ) {
          this.getSottoparagrafi(paragrafoElement.id);
          this.setState({
            modalTitle: 'Descrizione Reparti',
            currentTabValue: paragrafoElement,
          });
        } else if (
          paragrafoElement.obbligatorio &&
          paragrafoElement.label ===
            'Definizione delle procedure di autocontrollo'
        ) {
          this.getSottoparagrafi(paragrafoElement.id);
          this.setState({
            modalTitle: 'Definizione Procedure Autocontrollo',
            currentTabValue: paragrafoElement,
          });
        } else {
          if (paragrafoElement.tipo === 'PDF') {
            this.setState({
              currentTabValue: paragrafoElement,
              loading: false,
              loadingPDF: false,
              sottoParagrafiData: [],
              paragraphBlockState: false,
              sottoparagrafiLoading: false,
            });
          } else {
            this.setState(
              {
                modalTitle: 'Paragrafo ' + paragrafoElement.valueOfParagraph,
                currentTabValue: paragrafoElement,
              },
              () => {
                this.getSottoparagrafi(this.state.currentTabValue.id);
              },
            );
          }
        }
        this.setState({
          loading: false,
          loadingPDF: false,
          colonneTabellaCustom: [],
        });
      } else {
        let confirmation = window.confirm(
          'Modifiche non salvate, cambiare paragrafo?',
        );
        if (confirmation) {
          this.setState({
            edit: false,
            tabValue: newValue,
          });
          this.setState({
            showTables: false,
            sottoParagrafi: [],
            tabelle: [],
          });

          let paragrafoElement = this.state.tabs.at(newValue);
          if (
            paragrafoElement.obbligatorio &&
            paragrafoElement.label === 'Organigramma'
          ) {
            this.getSottoparagrafi(paragrafoElement.id);
            this.setState({
              modalTitle: 'Organigramma',
              currentTabValue: paragrafoElement,
            });
          } else if (
            paragrafoElement.obbligatorio &&
            paragrafoElement.label === 'Definizione dei reparti'
          ) {
            this.getSottoparagrafi(paragrafoElement.id);
            this.setState({
              modalTitle: 'Definizione Reparti',
              currentTabValue: paragrafoElement,
            });
          } else if (
            paragrafoElement.obbligatorio &&
            paragrafoElement.label === 'Descrizione dei reparti'
          ) {
            this.getSottoparagrafi(paragrafoElement.id);
            this.setState({
              modalTitle: 'Descrizione Reparti',
              currentTabValue: paragrafoElement,
            });
          } else if (
            paragrafoElement.obbligatorio &&
            paragrafoElement.label ===
              'Definizione delle procedure di autocontrollo'
          ) {
            this.getSottoparagrafi(paragrafoElement.id);
            this.setState({
              modalTitle: 'Definizione Procedure Autocontrollo',
              currentTabValue: paragrafoElement,
            });
          } else {
            if (paragrafoElement.tipo === 'PDF') {
              this.setState({
                currentTabValue: paragrafoElement,
                loading: false,
                sottoParagrafiData: [],
                paragraphBlockState: false,
                sottoparagrafiLoading: false,
              });
            } else {
              this.setState(
                {
                  modalTitle: 'Paragrafo ' + paragrafoElement.valueOfParagraph,
                  currentTabValue: paragrafoElement,
                },
                () => {
                  this.getSottoparagrafi(paragrafoElement.id);
                },
              );
            }
          }
          this.setState({
            loading: false,
            colonneTabellaCustom: [],
          });
        } else {
          this.setState({ loading: false, loadingPDF: false });
        }
      }
    }
  };

  handleTabChangeNew = (newValueItem, chapter) => {
    this.setState({
      isTabChangePressed: true,
    });
    let newValue = newValueItem;
    let tabList = [...this.state.tabListValues];
    tabList.forEach((item) => {
      item.paragraphData.forEach((item2) => {
        item2.isTabSelected = false;
      });
    });
    tabList.forEach((item, index) => {
      if (index === chapter) {
        item.paragraphData.forEach((item2, index2) => {
          if (index2 === newValueItem) {
            item2.isTabSelected = !item2.isTabSelected;
            item.isTabOpen = true;
          }
        });
      }
    });
    this.setState({
      loading: true,
      loadingPDF: true,
      editParagraphTitle: false,
      tabValue: newValue,
      edit: false,
      showTables: false,
      sottoParagrafi: [],
      pdfLoadedPage: 1,
      tabelle: [],
      modifiedPDFFile: null,
      modifiedPDFFileUrl: null,
    });

    let paragrafoElement =
      this.state.tabListValues[chapter].paragraphData.at(newValueItem);
    if (
      paragrafoElement.obbligatorio &&
      paragrafoElement.label === 'Organigramma'
    ) {
      this.getSottoparagrafi(paragrafoElement.id);
      this.setState({
        modalTitle: 'Organigramma',
        currentTabValue: paragrafoElement,
      });
    } else if (
      paragrafoElement.obbligatorio &&
      paragrafoElement.label === 'Definizione dei reparti'
    ) {
      this.getSottoparagrafi(paragrafoElement.id);
      this.setState({
        modalTitle: 'Definizione Reparti',
        currentTabValue: paragrafoElement,
      });
    } else if (
      paragrafoElement.obbligatorio &&
      paragrafoElement.label === 'Descrizione dei reparti'
    ) {
      this.getSottoparagrafi(paragrafoElement.id);
      this.setState({
        modalTitle: 'Descrizione Reparti',
        currentTabValue: paragrafoElement,
      });
    } else if (
      paragrafoElement.obbligatorio &&
      paragrafoElement.label === 'Definizione delle procedure di autocontrollo'
    ) {
      this.getSottoparagrafi(paragrafoElement.id);
      this.setState({
        modalTitle: 'Definizione Procedure Autocontrollo',
        currentTabValue: paragrafoElement,
      });
    } else {
      if (paragrafoElement.tipo === 'PDF') {
        this.setState({
          currentTabValue: paragrafoElement,
          loading: false,
          loadingPDF: false,
          sottoParagrafiData: [],
          paragraphBlockState: false,
          modifiedPDFFile: null,
          modifiedPDFFileUrl: null,
          sottoparagrafiLoading: false,
        });
      } else {
        this.setState(
          {
            modalTitle: 'Paragrafo ' + paragrafoElement.valueOfParagraph,
            currentTabValue: paragrafoElement,
          },
          () => {
            this.getSottoparagrafi(paragrafoElement.id);
          },
        );
      }
    }
    this.setState(
      {
        loading: false,
        loadingPDF: false,
        colonneTabellaCustom: [],
      },
      () => {
        setChapterIndex(chapter);
        setParagraphIndex(newValueItem);
      },
    );
  };

  handleTabChangeManually = (newValue) => {
    this.setState({
      loading: true,
      loadingPDF: true,
      tabValue: newValue,
      edit: false,
      showTables: false,
      sottoParagrafi: [],
      tabelle: [],
    });

    let paragrafoElement = this.state.tabs.at(newValue);
    if (
      paragrafoElement.obbligatorio &&
      paragrafoElement.label === 'Organigramma'
    ) {
      this.getSottoparagrafi(paragrafoElement.id);
      this.setState({
        modalTitle: 'Organigramma',
        currentTabValue: paragrafoElement,
      });
    } else if (
      paragrafoElement.obbligatorio &&
      paragrafoElement.label === 'Definizione dei reparti'
    ) {
      this.getSottoparagrafi(paragrafoElement.id);
      this.setState({
        modalTitle: 'Definizione Reparti',
        currentTabValue: paragrafoElement,
      });
    } else if (
      paragrafoElement.obbligatorio &&
      paragrafoElement.label === 'Descrizione dei reparti'
    ) {
      this.getSottoparagrafi(paragrafoElement.id);
      this.setState({
        modalTitle: 'Descrizione Reparti',
        currentTabValue: paragrafoElement,
      });
    } else if (
      paragrafoElement.obbligatorio &&
      paragrafoElement.label === 'Definizione delle procedure di autocontrollo'
    ) {
      this.getSottoparagrafi(paragrafoElement.id);
      this.setState({
        modalTitle: 'Definizione Procedure Autocontrollo',

        currentTabValue: paragrafoElement,
      });
    } else {
      if (paragrafoElement.tipo === 'PDF') {
        this.setState({
          currentTabValue: paragrafoElement,
          loading: false,
          loadingPDF: false,
          sottoParagrafiData: [],
          paragraphBlockState: false,
          sottoparagrafiLoading: false,
        });
      } else {
        this.setState(
          {
            modalTitle: 'Paragrafo ' + paragrafoElement.valueOfParagraph,
            currentTabValue: paragrafoElement,
          },
          () => {
            this.getSottoparagrafi(paragrafoElement.id);
          },
        );
      }
    }
    this.setState({
      loading: false,
      loadingPDF: false,
      colonneTabellaCustom: [],
    });
  };

  aggiornaTabellaCustomHandler = (recordData) => {
    this.setState({
      edit: true,
    });
  };

  aggiornaReparti = (repartiData) => {
    this.setState({
      updatedReparti: repartiData,
      edit: true,
    });
  };

  aggiornaRepartiForResponsabili = (repartiData) => {
    this.setState({
      updatedReparti: repartiData,
      // edit: true,
    });
  };

  launchRepartiUpdate = (reparti) => {
    updateReparti(this.state.idManuale, reparti)
      .then((result) => {
        this.setState(
          {
            updatedReparti: null,
          } /*,() => window.location.reload()*/,
        );
        if (this.state.chapterMode) {
          this.handleTabChangeNew(
            parseInt(getParagraphIndex()),
            parseInt(getChapterIndex()),
          );
        } else {
          this.handleTabChangeManually(this.state.tabValue);
        }
      })
      .catch((error) => {
        reportApiError(error);
        this.setState({
          isSaveButtonPressed: false,
        });
      });
  };

  handleSave = () => {
    this.setState({
      isSaveButtonPressed: true,
      loadingPDF: true,
      paragraphBlockState: false,
    });
    if (this.state.chapterMode === false) {
      const paragrafi = JSON.parse(JSON.stringify(this.state.tabs));
      let currentParagraph = JSON.parse(
        JSON.stringify(this.state.currentTabValue),
      );
      if (currentParagraph.label !== '') {
        const paragrafiToPass = [];
        paragrafi.forEach((item) => {
          paragrafiToPass.push({
            bloccato: item.isBlocked,
            id: item.id,
            manualeId: item.idManuale,
            numeroParagrafo: item.value,
            obbligatorio: item.obbligatorio,
            stato: item.stato,
            titolo: item.label,
            type: item.tipo === 'Testo' ? null : item.tipo,
            pdfId: item.pdfId,
          });
        });
        let requestBody = {
          manualeId: this.state.tabs.at(0).idManuale,
          paragrafi: paragrafiToPass,
        };
        let componentiToPassForSottoparagrafi = [
          ...this.state.sottoParagrafiData,
        ];
        componentiToPassForSottoparagrafi.forEach((item) => {
          item.immagini.forEach((item2, index) => {
            item2.posizione = index;
          });
        });
        let bodyToPassForSottoparagrafi = {
          id: currentParagraph.id,
          manualeId: currentParagraph.idManuale,
          titolo: currentParagraph.label,
          componenti: componentiToPassForSottoparagrafi,
        };
        putModificaParagrafiManualePersonalizzato(requestBody)
          .then((result) => {})
          .then((result) => {
            if (bodyToPassForSottoparagrafi.componenti.length > 0) {
              updateSottoparagrafi(bodyToPassForSottoparagrafi)
                .then((result) => {
                  if (this.state.updatedReparti !== null) {
                    this.launchRepartiUpdate(this.state.updatedReparti);
                  } else if (this.state.updatedLocali !== null) {
                    if (
                      this.state.updatedLocali.some((item) => item.nome === '')
                    ) {
                      alert('Attenzione, uno dei locali è privo di nome');
                    } else {
                      this.launchLocaliUpdate(this.state.updatedLocali);
                    }
                  } else {
                    this.getParagrafiManualeInCostruzioneAfterSave();
                  }
                })
                .catch((error) => {
                  reportApiError(error);
                  this.setState({
                    isSaveButtonPressed: false,
                  });
                });
            } else {
              this.setState({
                isSaveButtonPressed: false,
                edit: false,
                loadingPDF: false,
              });
            }
          })
          .catch((error) => {
            reportApiError(error);
            this.setState({
              isSaveButtonPressed: false,
            });
          });
      } else {
        alert('Attenzione, aggiungere un paragrafo da salvare!');
        this.setState({
          isSaveButtonPressed: false,
        });
      }
    } else {
      const capitoli = JSON.parse(JSON.stringify(this.state.tabListValues));
      let idManuale = this.props.manualeId;
      let capitoliToPass = [];
      capitoli.forEach((item) => {
        let objectToPushInCapitolo = {};
        let paragrafi = [];
        let capitolo = {
          id: item.id,
          manualeId: idManuale,
          numeroCapitolo: item.numeroCapitolo,
          titolo: item.name,
          stato: null,
        };
        item.paragraphData.forEach((item2) => {
          let paragrafo = {
            id: item2.id,
            manualeId: idManuale,
            capitoloId: item.id,
            numeroParagrafo: item2.value,
            titolo: item2.label,
            stato: item2.stato,
            obbligatorio: item2.obbligatorio,
            bloccato: item2.isBlocked,
            type: item2.tipo === 'Testo' ? null : item2.tipo,
            pdfId: item2.pdfId,
          };
          paragrafi.push(paragrafo);
        });
        objectToPushInCapitolo = {
          capitolo: capitolo,
          paragrafi: paragrafi,
        };
        capitoliToPass.push(objectToPushInCapitolo);
      });

      let bodyToSave = {
        manualeId: idManuale,
        capitoli: capitoliToPass,
      };
      putCapitoli(bodyToSave)
        .then((result) => {
          let currentParagraph = this.state.currentTabValue;
          let componentiToPassForSottoparagrafi = [
            ...this.state.sottoParagrafiData,
          ];
          let bodyToPassForSottoparagrafi = {
            id: currentParagraph.id,
            manualeId: currentParagraph.idManuale,
            titolo: currentParagraph.label,
            componenti: componentiToPassForSottoparagrafi,
          };
          componentiToPassForSottoparagrafi.forEach((item) => {
            item.immagini.forEach((item2, index) => {
              item2.posizione = index;
            });
          });
          if (bodyToPassForSottoparagrafi.componenti.length > 0) {
            updateSottoparagrafi(bodyToPassForSottoparagrafi)
              .then((result) => {
                if (this.state.updatedReparti !== null) {
                  this.launchRepartiUpdate(this.state.updatedReparti);
                } else if (this.state.updatedLocali !== null) {
                  if (
                    this.state.updatedLocali.some((item) => item.nome === '')
                  ) {
                    alert('Attenzione, uno dei locali è privo di nome');
                  } else {
                    this.launchLocaliUpdate(this.state.updatedLocali);
                  }
                } else {
                  this.getParagrafiManualeInCostruzioneAfterSaveForChapters();
                }
              })
              .catch((error) => {
                reportApiError(error);
                this.setState({
                  isSaveButtonPressed: false,
                });
              });
          } else {
            this.setState({
              isSaveButtonPressed: false,
              loadingPDF: false,
              paragraphBlockState: false,
              edit: false,
            });
          }
        })
        .catch((error) => {
          reportApiError(error);
          this.setState({
            isSaveButtonPressed: false,
          });
        });
    }
  };

  aggiornaLocali = (records) => {
    let recordsToChange = [...records];
    this.setState({
      updatedLocali: recordsToChange,
      edit: true,
    });
  };

  launchLocaliUpdate = (locali) => {
    let recordsToSend = [...locali];
    recordsToSend.forEach((item) => {
      delete item['nomeReparto'];
      delete item['repartiToSelect'];
    });
    let body = {
      manualeId: this.state.idManuale,
      locali: recordsToSend,
    };
    updateLocaliManuale(body)
      .then((result) => {
        this.setState(
          {
            updatedLocali: null,
          } /*,() => window.location.reload()*/,
        );
        if (!this.state.chapterMode) {
          this.handleTabChangeManually(this.state.tabValue);
        } else {
          this.handleTabChangeNew(
            parseInt(getParagraphIndex()),
            parseInt(getChapterIndex()),
          );
        }
      })
      .catch((error) => {
        reportApiError(error);
        this.setState({
          isSaveButtonPressed: false,
        });
      });
  };

  bringDataToParent = (data, index) => {
    let newSubparagraphArray = [...this.state.sottoParagrafiData];
    let elementToChange = newSubparagraphArray.filter(
      (item) => item.numeroComponente === index,
    );
    elementToChange[0].componenti[0].contenuto = data;
    this.setState(
      {
        sottoParagrafiData: newSubparagraphArray,
      },
      () => {
        this.handleEditChange();
      },
    );
  };

  bringDataToParentAutocontrollo = (data, index) => {
    let newSubparagraphArray = [...this.state.sottoParagrafiData];
    let elementToChange = newSubparagraphArray.filter(
      (item) => item.numeroComponente === index,
    );
    elementToChange[0].componenti = data.componenti[1].componenti;
    this.setState({
      sottoParagrafiData: newSubparagraphArray,
    });
  };

  bringDataToParentAutocontrolloAfterExchange = (data, index) => {
    let newSubparagraphArray = [...this.state.sottoParagrafiData];
    let elementToChange = newSubparagraphArray.filter(
      (item) => item.numeroComponente === index,
    );
    elementToChange[0].componenti = data;
    this.setState({
      sottoParagrafiData: newSubparagraphArray,
    });
  };

  bringDataToParentAutocontrolloOrganigramma = (data, index) => {
    let newSubparagraphArray = [...this.state.sottoParagrafiData];
    let elementToChange = newSubparagraphArray.filter(
      (item) => item.numeroComponente === index,
    );
    elementToChange[0].componenti = data;
    this.setState({
      sottoParagrafiData: newSubparagraphArray,
    });
  };

  bringTextToParentAutocontrollo = (data, index) => {
    let newSubparagraphArray = [...this.state.sottoParagrafiData];
    let elementToChange = newSubparagraphArray.filter(
      (item) => item.numeroComponente === index,
    );
    elementToChange[0].componenti = data[1].componenti;
    this.setState({
      sottoParagrafiData: newSubparagraphArray,
    });
  };

  bringTitleToParent = (title, index) => {
    let newSubparagraphArray = [...this.state.sottoParagrafiData];
    let elementToChange = newSubparagraphArray.filter(
      (item) => item.numeroComponente === index,
    );
    elementToChange[0].titolo = title;
    this.setState(
      {
        sottoParagrafiData: newSubparagraphArray,
      },
      () => {
        this.handleEditChange();
      },
    );
  };

  bringImageToParent = (image, names, index) => {
    let newSubparagraphArray = [...this.state.sottoParagrafiData];
    let elementToChange = newSubparagraphArray.filter(
      (item) => item.numeroComponente === index,
    );
    postUploadImmaginiSottoparagrafi(image[image.length - 1])
      .then((result) => {
        elementToChange[0].immagini.push(result);
        this.setState({
          sottoParagrafiData: newSubparagraphArray,
        });
      })
      .catch((error) => {
        reportApiError(error);
      });
  };

  deleteImageFromSottoparagrafo = (immagini, index) => {
    let newSubparagraphArray = [...this.state.sottoParagrafiData];
    newSubparagraphArray[index].immagini = immagini;
    this.setState({
      sottoParagrafiData: newSubparagraphArray,
    });
  };

  onCreazioneTabellaModalClose = () => {
    this.setState({
      isModalOpen: false,
      colonneTabellaCustom: [],
    });
  };

  onColumnChange = (e, index) => {
    let columnName = e.target.value;
    if (columnName === '') {
      columnName = ' ';
    }
    let array = this.state.colonneTabellaCustom;
    array[index].header = columnName;
    this.setState({
      colonneTabellaCustom: array,
    });
  };

  onDeleteColumn = (index) => {
    let columnArray = [...this.state.colonneTabellaCustom];
    columnArray.splice(index, 1);
    this.setState({
      colonneTabellaCustom: columnArray,
    });
  };

  onAddColumn = () => {
    if (this.state.colonneTabellaCustom.length < 21) {
      let updateColumn = [...this.state.colonneTabellaCustom];
      let valueToSave = this.state.colonneTabellaCustom.length;
      updateColumn.push({
        header: 'Colonna ' + valueToSave++,
        id: 'Colonna' + valueToSave++,
      });
      this.setState({
        colonneTabellaCustom: updateColumn,
      });
    } else {
      alert('Errore, sono state inserite più di 21 colonne');
    }
  };

  handleAggiuntaTable = () => {
    if (this.state.colonneTabellaCustom.length > 0) {
      let tablesArray = [...this.state.sottoParagrafi];
      let currentParagraph = this.state.currentTabValue;
      let nameToPass = 'Tabella Personalizzata';
      let subParagraphDataArray = [...this.state.sottoParagrafiData];
      let columnsToInsert = [...this.state.colonneTabellaCustom];
      let columnsNames = [];
      columnsToInsert.forEach((element) => {
        columnsNames.push(element.header);
      });
      let logicalArrayOfColumns = '';
      let data = [];
      let logicalArrayOfData = [];
      logicalArrayOfColumns = JSON.stringify(columnsNames);
      logicalArrayOfData = JSON.stringify(data);
      if (this.state.tipoDiTabella === 'Custom') {
        let newSubparagraphArrayData = {
          tipo: 'SOTTOPARAGRAFO',
          id: null,
          paragrafoId: currentParagraph.id,
          parentComponentId: null,
          numeroComponente: this.state.sottoParagrafoIndex,
          titolo: 'Tabella Personalizzata',
          numeroSottoparagrafo: null,
          tipoContenuto: null,
          componenti: [
            {
              tipo: 'TABELLA_PERSONALIZZATA',
              id: null,
              paragrafoId: currentParagraph.id,
              parentComponentId: null,
              numeroComponente: this.state.sottoParagrafoIndex,
              definizioniColonne: logicalArrayOfColumns,
              contenuto: logicalArrayOfData,
            },
          ],
          immagini: [],
        };
        this.setState({
          sottoParagrafiData: subParagraphDataArray,
        });
        tablesArray.push({
          index: this.state.sottoParagrafoIndex,
          value: (
            <CustomTableForManuale
              theme={this.props.theme}
              key={this.state.sottoParagrafoIndex}
              isParagrafoBlocked={this.state.currentTabValue.isBlocked}
              mode={'Custom'}
              updateCustomTable={this.aggiornaTabellaCustomHandler}
              moveUpward={this.handleSottoparagrafoMovementUpward}
              moveDownward={this.handleSottoparagrafoMovementDownward}
              titoloParagrafo={
                this.state.currentTabValue.label
                  ? this.state.currentTabValue.label
                  : 'Nuovo Paragrafo'
              }
              idParagrafo={null}
              sottoParagrafiData={subParagraphDataArray}
              modalTitle={nameToPass}
              titoloInizialeSottoparagrafo={nameToPass}
              rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
              bringTitleToParent={this.bringTitleToParent}
              idManuale={this.state.idManuale}
              indexTab={this.state.sottoParagrafoIndex}
              handleRimuoviTabella={this.handleRimuoviTabella}
              columns={this.state.colonneTabellaCustom}
              numberOfRows={this.state.numberOfLines}
            />
          ),
        });
        subParagraphDataArray.push(newSubparagraphArrayData);

        let newIndex = this.state.sottoParagrafoIndex;
        newIndex += 1;
        this.setState({
          sottoParagrafoIndex: newIndex,
        });
      }
      this.setState({
        sottoParagrafi: tablesArray,
        colonneTabellaCustom: [],
        showTables: true,
        isModalOpen: false,
        edit: true,
      });
    } else {
      alert('Attenzione, occorre aggiungere almeno una colonna');
    }
  };

  handleAggiuntaSottoparagrafi = () => {
    if (
      !this.state.currentTabValue.isBlocked ||
      localStorage.getItem('superconsulente') === 'true' ||
      localStorage.getItem('consulenteId') === 'null'
    ) {
      let numberToIncrease = this.state.numberOfSottoparagrafo;
      let subParagraphArray = [...this.state.sottoParagrafi];
      let currentParagraph = this.state.currentTabValue;
      let subParagraphDataArray = [...this.state.sottoParagrafiData];
      if (this.state.modalitaSottoparagrafo === 'Testo') {
        let newSubparagraphArrayData = {
          tipo: 'SOTTOPARAGRAFO',
          id: null,
          paragrafoId: currentParagraph.id,
          parentComponentId: null,
          numeroComponente: this.state.sottoParagrafoIndex,
          titolo: null,
          numeroSottoparagrafo: null,
          tipoContenuto: null,
          componenti: [
            {
              tipo: 'BLOCCO_TESTO',
              id: null,
              paragrafoId: currentParagraph.id,
              parentComponentId: null,
              numeroComponente: this.state.sottoParagrafoIndex,
              titolo: '',
              contenuto: '',
            },
          ],
          immagini: [],
        };
        subParagraphDataArray.push(newSubparagraphArrayData);
        subParagraphArray.push({
          index: this.state.sottoParagrafoIndex,
          value: (
            <NewSottoparagrafoTesto
              isParagrafoBlocked={this.state.currentTabValue.isBlocked}
              bringDataToParent={this.bringDataToParent}
              bringTitleToParent={this.bringTitleToParent}
              indexValue={this.state.sottoParagrafoIndex}
              componentHeight={'50%'}
              componentWidth={'50%'}
              rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
              titoloInizialeSottoparagrafo={this.state.titoloSottoParagrafo}
            />
          ),
        });
        let newIndex = this.state.sottoParagrafoIndex;
        newIndex += 1;
        this.setState({
          sottoParagrafoIndex: newIndex,
        });
      }
      if (this.state.modalitaSottoparagrafo === 'ImmagineAndTesto') {
        let newSubparagraphArrayData = {
          tipo: 'SOTTOPARAGRAFO',
          id: null,
          paragrafoId: currentParagraph.id,
          parentComponentId: null,
          numeroComponente: this.state.sottoParagrafoIndex,
          titolo: null,
          numeroSottoparagrafo: null,
          tipoContenuto: null,
          componenti: [
            {
              tipo: 'BLOCCO_TESTO',
              id: null,
              paragrafoId: currentParagraph.id,
              parentComponentId: null,
              numeroComponente: this.state.sottoParagrafoIndex,
              titolo: '',
              contenuto: '',
            },
          ],
          immagini: [],
        };
        subParagraphDataArray.push(newSubparagraphArrayData);
        this.setState({
          sottoParagrafiData: subParagraphDataArray,
        });
        subParagraphArray.push({
          index: this.state.sottoParagrafoIndex,
          value: (
            <NewSottoparagrafoTestoEImmagine
              theme={this.props.theme}
              key={this.state.sottoParagrafoIndex}
              handleEditChange={this.handleEditChange}
              isParagrafoBlocked={this.state.currentTabValue.isBlocked}
              sottoParagrafiData={subParagraphDataArray}
              immaginiParagrafo={[]}
              deleteImage={this.deleteImageFromSottoparagrafo}
              moveUpward={this.handleSottoparagrafoMovementUpward}
              moveDownward={this.handleSottoparagrafoMovementDownward}
              firstElement={false}
              indexValue={this.state.sottoParagrafoIndex}
              bringDataToParent={this.bringDataToParent}
              bringTitleToParent={this.bringTitleToParent}
              titoloInizialeSottoparagrafo={this.state.titoloSottoParagrafo}
              testoSottoParagrafo={''}
              rimuoviSottoparagrafo={this.handleRimuoviSottoparagrafo}
              idImmagine={numberToIncrease}
            />
          ),
        });
        let newIndex = this.state.sottoParagrafoIndex;
        newIndex += 1;
        this.setState({
          sottoParagrafoIndex: newIndex,
        });
      }
      numberToIncrease += 1;
      this.setState(
        {
          numberOfSottoparagrafo: numberToIncrease,
          sottoParagrafi: subParagraphArray,
          isSottoparagrafiModalOpen: false,
          edit: true,
          titoloSottoParagrafo: 'Nuovo Sottoparagrafo',
        },
        () => {
          // Rebuilds every sottoparagrafo element from current
          // sottoParagrafiData, instead of just the one appended above -
          // otherwise previously-rendered siblings keep a stale
          // sottoParagrafiData prop reference from whenever they were
          // created, which breaks any prop derived from array length
          // (e.g. the move-down button's disabled state) until a full
          // reload rebuilds the list from scratch.
          this.loadSottoparagrafiAfterSwap();
        },
      );
    } else {
      alert(
        'Attenzione, operazione non permessa poichè il paragrafo è bloccato',
      );
    }
  };

  handleRimuoviSottoparagrafo = (indexValue) => {
    if (
      !this.state.currentTabValue.isBlocked ||
      localStorage.getItem('superconsulente') === 'true' ||
      localStorage.getItem('consulenteId') === 'null'
    ) {
      // Filter out the elements to remove
      const newSubparagraphArray = this.state.sottoParagrafi
        .filter((item) => item.index !== indexValue)
        .map((item, index) => ({
          ...item,
          index: index, // Refresh the indexes
        }));

      const newSubparagraphArrayData = this.state.sottoParagrafiData
        .filter((_, index) => index !== indexValue)
        .map((item, index) => ({
          ...item,
          numeroComponente: index, // Refresh the component numbers
        }));

      // Compute the new index
      const newIndex = Math.max(0, this.state.sottoParagrafoIndex - 1);

      // Update the state in a single call
      this.setState(
        {
          sottoParagrafi: newSubparagraphArray,
          sottoParagrafiData: newSubparagraphArrayData,
          sottoParagrafoIndex: newIndex,
          edit: true,
        },
        () => {
          // Reload the subparagraphs to make sure everything is up to date
          this.loadSottoparagrafiAfterSwap();
        },
      );
    } else {
      alert(
        'Attenzione, operazione non permessa poichè il paragrafo è bloccato',
      );
    }
  };

  handleRimuoviTabella = (indexValue) => {
    if (
      !this.state.currentTabValue.isBlocked ||
      localStorage.getItem('superconsulente') === 'true' ||
      localStorage.getItem('consulenteId') === 'null'
    ) {
      const newSubparagraphArray = this.state.sottoParagrafi
        .filter((item) => item.index !== indexValue)
        .map((item, index) => ({
          ...item,
          index: index,
        }));

      const newSubparagraphArrayData = this.state.sottoParagrafiData
        .filter((_, index) => index !== indexValue)
        .map((item, index) => ({
          ...item,
          numeroComponente: index,
        }));

      const newIndex = Math.max(0, this.state.sottoParagrafoIndex - 1);

      this.setState(
        {
          sottoParagrafi: newSubparagraphArray,
          sottoParagrafiData: newSubparagraphArrayData,
          sottoParagrafoIndex: newIndex,
          edit: true,
        },
        () => {
          this.loadSottoparagrafiAfterSwap();
        },
      );
    } else {
      alert(
        'Attenzione, operazione non permessa poichè il paragrafo è bloccato',
      );
    }
  };

  handleSottoparagrafoMovementUpward = (sottoParagrafoIndex) => {
    if (
      !this.state.currentTabValue.isBlocked ||
      localStorage.getItem('superconsulente') === 'true' ||
      localStorage.getItem('consulenteId') === 'null'
    ) {
      const cleanArray = this.state.sottoParagrafiData.filter(
        (item) => item !== undefined,
      );
      if (sottoParagrafoIndex > 1 && sottoParagrafoIndex < cleanArray.length) {
        const newArray = [...cleanArray];
        const temp = newArray[sottoParagrafoIndex];
        newArray[sottoParagrafoIndex] = newArray[sottoParagrafoIndex - 1];
        newArray[sottoParagrafoIndex - 1] = temp;

        const updatedArray = newArray.map((item, index) => ({
          ...item,
          numeroComponente: index,
        }));

        this.setState(
          {
            sottoParagrafiData: updatedArray,
          },
          () => {
            this.loadSottoparagrafiAfterSwap();
          },
        );
      }
    } else {
      alert(
        'Attenzione, operazione non permessa poichè il paragrafo è bloccato',
      );
    }
  };

  handleSottoparagrafoMovementDownward = (sottoParagrafoIndex) => {
    if (
      !this.state.currentTabValue.isBlocked ||
      localStorage.getItem('superconsulente') === 'true' ||
      localStorage.getItem('consulenteId') === 'null'
    ) {
      // Filter out any undefined and build a clean copy
      const cleanArray = this.state.sottoParagrafiData.filter(
        (item) => item !== undefined,
      );

      if (
        sottoParagrafoIndex >= 0 &&
        sottoParagrafoIndex < cleanArray.length - 1
      ) {
        // Build a new array with the two elements swapped
        const newArray = [...cleanArray];
        const temp = newArray[sottoParagrafoIndex];
        newArray[sottoParagrafoIndex] = newArray[sottoParagrafoIndex + 1];
        newArray[sottoParagrafoIndex + 1] = temp;

        // Refresh the component numbers
        const updatedArray = newArray.map((item, index) => ({
          ...item,
          numeroComponente: index,
        }));

        this.setState(
          {
            sottoParagrafiData: updatedArray,
          },
          () => {
            this.loadSottoparagrafiAfterSwap();
          },
        );
      }
    } else {
      alert(
        'Attenzione, operazione non permessa poichè il paragrafo è bloccato',
      );
    }
  };

  openModalToChangeChapterName = (chapterData, chapterIndex) => {
    let chapterToSave = { ...chapterData };
    this.setState({
      isChangeChapterNameModalOpen: true,
      selectedChapter: chapterToSave,
      selectedChapterIndex: chapterIndex,
    });
  };

  closeModalToChangeChapterName = () => {
    this.setState({
      isChangeChapterNameModalOpen: false,
      selectedChapterIndex: null,
    });
  };

  // The modal snapshots the chapter when it opens, so its copy can still carry
  // the id: null a chapter has before putCapitoli's reload assigns the backend
  // one. Position is the only handle left in that window, and it is a reliable
  // one: putCapitoli renumbers numeroCapitolo from the payload's order and the
  // reload re-fetches sorted by it, so a chapter keeps its slot. With a real
  // id, only the id may decide - falling back to a position then would rename
  // whichever chapter now sits there.
  resolveChapterIndexForRename = (chapter) => {
    const chapters = this.state.tabListValues;
    if (chapter.id != null) {
      return chapters.findIndex((item) => item.id === chapter.id);
    }
    const indexByPosition = this.state.selectedChapterIndex;
    return indexByPosition != null && chapters[indexByPosition]
      ? indexByPosition
      : -1;
  };

  saveChapterName = (chapter) => {
    const index = this.resolveChapterIndexForRename(chapter);
    if (index === -1) {
      this.closeModalToChangeChapterName();
      return;
    }
    const chapters = [...this.state.tabListValues];
    chapters[index] = { ...chapters[index], name: chapter.name };
    this.setState(
      {
        tabListValues: chapters,
      },
      () => {
        this.updateParagrafiForChapterMode();
        this.closeModalToChangeChapterName();
      },
    );
  };

  handleToggleLock = (item) => {
    let tabsToChange = [...this.state.tabs];
    const index = tabsToChange.findIndex((e) => e.value === item.value);
    if (index !== -1) {
      tabsToChange[index] = { ...item, isBlocked: !item.isBlocked };
      this.setState({
        tabs: tabsToChange,
      });
    }
  };

  handleMoveUp = (item) => {
    const index = this.state.tabs.findIndex((o) => o.value === item.value);
    if (index > 0) {
      let newTabs = [...this.state.tabs];
      swapTabElements(newTabs, index, index - 1);

      const newTabValue =
        this.state.tabValue === index
          ? index - 1
          : this.state.tabValue === index - 1
            ? index
            : this.state.tabValue;

      this.setState(
        {
          tabs: newTabs,
          tabValue: newTabValue,
        },
        () => {
          this.handleTabChangeManually(newTabValue);
        },
      );
    }
  };

  handleMoveDown = (item) => {
    let index = this.state.tabs.findIndex((o) => o.value === item.value);
    if (index < this.state.tabs.length - 1) {
      let newTabs = [...this.state.tabs];
      swapTabElements(newTabs, index, index + 1);

      const newTabValue =
        this.state.tabValue === index
          ? index + 1
          : this.state.tabValue === index + 1
            ? index
            : this.state.tabValue;

      this.setState(
        {
          tabs: newTabs,
          tabValue: newTabValue,
        },
        () => {
          this.handleTabChangeManually(newTabValue);
        },
      );
    }
  };

  handleDeleteTab = (tab) => {
    if (this.state.paragraphBlockState) return;

    const { value } = tab;

    this.setState(
      (prevState) => {
        const updatedTabs = prevState.tabs.filter(
          (item) => item.value !== value,
        );

        if (updatedTabs.length !== prevState.tabs.length) {
          setChapterIndex(0);
          setParagraphIndex(0);
        }

        return {
          tabs: updatedTabs,
          numberOfParagrafi: prevState.numberOfParagrafi - 1,
          paragraphBlockState: true,
        };
      },
      function () {
        this.updateParagrafi();
      },
    );
  };

  // Both fields of the title are written here: updateParagrafi and
  // updateParagrafiForChapterMode persist `label` as the titolo, the nav tree
  // renders `nomeTab`, and the editor field reads currentTabValue.label.
  // currentTabValue is left pointing at the same object the list holds, the way
  // handleTabChangeNew sets it: swapChapterElements and
  // toggleParagraphLockChapters mutate the list entry in place and expect the
  // open editor to see it.
  handleParagraphTitleChange = (newTitle) => {
    const current = this.state.currentTabValue;
    if (this.state.chapterMode) {
      const chapters = [...this.state.tabListValues];
      const chapter = chapters[current.chapterIndex];
      const index = chapter
        ? chapter.paragraphData.findIndex((item) => item.id === current.id)
        : -1;
      if (index === -1) {
        // chapterIndex can outlive the list it indexes: deleting a chapter
        // splices tabListValues without renumbering the paragraphs of the
        // chapters that follow, so until putCapitoli's reload rebuilds them the
        // editor may point at a chapter that does not hold this paragraph.
        // Take the title into the field and leave the list to the reload.
        this.setState({
          currentTabValue: { ...current, label: newTitle },
          edit: true,
        });
        return;
      }
      const paragraphData = [...chapter.paragraphData];
      const renamed = this.renameParagraphEntry(paragraphData[index], newTitle);
      paragraphData[index] = renamed;
      chapters[current.chapterIndex] = { ...chapter, paragraphData };
      this.setState({
        currentTabValue: renamed,
        tabListValues: chapters,
        edit: true,
      });
      return;
    }
    const tabs = [...this.state.tabs];
    const index = tabs.findIndex((item) => item.id === current.id);
    if (index === -1) {
      this.setState({
        currentTabValue: { ...current, label: newTitle },
        edit: true,
      });
      return;
    }
    const renamed = this.renameParagraphEntry(tabs[index], newTitle);
    tabs[index] = renamed;
    this.setState({ currentTabValue: renamed, tabs, edit: true });
  };

  renameParagraphEntry = (entry, newTitle) => ({
    ...this.state.currentTabValue,
    ...entry,
    label: newTitle,
    nomeTab: newTitle,
  });

  handleToggleEdit = () => {
    this.setState((prevState) => ({
      editParagraphTitle: !prevState.editParagraphTitle,
    }));
  };

  render() {
    const { classes } = this.props;
    const { tabs } = this.state;

    const isConsulente =
      localStorage.getItem('superconsulente') !== 'true' &&
      localStorage.getItem('consulenteId') !== 'null' &&
      localStorage.getItem('consulenteId') !== null;

    const currentPdfUrl =
      this.state.modifiedPDFFile !== null
        ? this.state.modifiedPDFFileUrl
        : BASE_URL + '/api/files?id=' + this.state.currentTabValue.pdfId;

    return (
      <div className={classes.mainContainer}>
        <UnsavedChangesNavigationGuard
          active={this.state.edit}
          message={UNSAVED_CHANGES_LEAVE_MESSAGE}
        />
        <div className={classes.pageShell}>
          <div className={classes.pageHeader}>
            <Typography className={classes.title} variant="h4">
              Nuovo manuale personalizzato
            </Typography>
          </div>

          {!this.state.loading ? (
            <>
              <div className={classes.root}>
                <ThemeProvider
                  theme={createMuiTheme(
                    {
                      overrides: {
                        MuiTabs: {
                          indicator: {
                            display: 'none',
                          },
                        },
                        MuiListItem: {
                          button: {
                            alignItems: 'stretch',
                          },
                        },
                        MuiTab: {
                          root: {
                            maxWidth: '100%',
                            minHeight: 'auto',
                            height: 'auto',
                            overflow: 'visible',
                            alignItems: 'stretch',
                            whiteSpace: 'normal',
                            paddingTop: this.props.theme.spacing(1.25),
                            paddingBottom: this.props.theme.spacing(1.25),
                            marginLeft: 0,
                            textTransform: 'none',
                          },
                          wrapper: {
                            width: '100%',
                            overflow: 'visible',
                          },
                        },
                      },
                    },

                    this.props.theme,
                  )}
                >
                  <div
                    className={`${classes.panelCard} ${classes.navigationPanel}`}
                  >
                    <div
                      className={`${classes.panelHeader} ${classes.navigationPanelHeader}`}
                    >
                      <div className={classes.panelTitleRow}>
                        <Typography className={classes.panelTitle} variant="h6">
                          Struttura manuale
                        </Typography>
                        <Tooltip
                          title={
                            <>
                              Seleziona un capitolo o un paragrafo per
                              modificarne il contenuto. L'icona del lucchetto
                              nell'elenco paragrafi blocca o sblocca la modifica
                              per i consulenti.
                            </>
                          }
                          placement="right"
                          enterTouchDelay={0}
                          classes={{ tooltip: classes.structureInfoTooltip }}
                        >
                          <IconButton
                            size="small"
                            className={classes.panelInfoButton}
                            aria-label="Informazioni sulla struttura manuale"
                          >
                            <InfoOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>

                    {this.state.chapterMode ? (
                      <>
                        <div className={classes.navigationScrollArea}>
                          <List className={classes.chapterList}>
                            {this.state.tabListValues.map((value, index) => {
                              return (
                                <Fragment key={index}>
                                  <ListItem
                                    button
                                    key={index}
                                    className={classes.navItem}
                                    onClick={(event) =>
                                      this.handleChapterOpening(event, index)
                                    }
                                  >
                                    <div className={classes.navItemContent}>
                                      <div className={classes.navItemHeader}>
                                        <div className={classes.navExpandSlot}>
                                          {value.isTabOpen ? (
                                            <ExpandLess
                                              className={
                                                classes.navItemExpandIcon
                                              }
                                              fontSize="small"
                                            />
                                          ) : (
                                            <ExpandMore
                                              className={
                                                classes.navItemExpandIcon
                                              }
                                              fontSize="small"
                                            />
                                          )}
                                        </div>
                                        <div className={classes.navItemMain}>
                                          <div
                                            className={
                                              classes.navItemTitleBlock
                                            }
                                          >
                                            <div
                                              className={
                                                classes.navItemTitleRow
                                              }
                                            >
                                              <StructureNavLabel
                                                text={value.name}
                                                className={classes.navItemText}
                                                tooltipClassName={
                                                  classes.structureInfoTooltip
                                                }
                                              />
                                              <NavActionButton
                                                title="Modifica nome capitolo"
                                                className={
                                                  classes.navItemEditButton
                                                }
                                                onClick={() => {
                                                  if (this.state.edit) {
                                                    let confirmation =
                                                      window.confirm(
                                                        'Modifiche non salvate, continuare?',
                                                      );
                                                    if (confirmation) {
                                                      this.openModalToChangeChapterName(
                                                        value,
                                                        index,
                                                      );
                                                    }
                                                  } else {
                                                    this.openModalToChangeChapterName(
                                                      value,
                                                      index,
                                                    );
                                                  }
                                                }}
                                              >
                                                <Edit
                                                  className={
                                                    classes.utilityActionIcon
                                                  }
                                                  fontSize="small"
                                                />
                                              </NavActionButton>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className={classes.navActionsColumn}>
                                        <div className={classes.navActionSlot}>
                                          <NavActionButton
                                            title="Elimina capitolo"
                                            className={classes.navActionButton}
                                            onClick={() => {
                                              this.handleChapterElimination(
                                                index,
                                              );
                                            }}
                                          >
                                            <DeleteOutline
                                              className={
                                                classes.destructiveActionIcon
                                              }
                                              fontSize="small"
                                            />
                                          </NavActionButton>
                                        </div>
                                        <div className={classes.navMoveGroup}>
                                          <NavActionButton
                                            title="Sposta giù"
                                            className={classes.navMoveButton}
                                            onClick={() =>
                                              this.handleChapterMovementDownward(
                                                index,
                                              )
                                            }
                                          >
                                            <ArrowDownward
                                              className={
                                                classes.utilityActionIcon
                                              }
                                              fontSize="small"
                                            />
                                          </NavActionButton>
                                          <NavActionButton
                                            title="Sposta su"
                                            className={classes.navMoveButton}
                                            onClick={() =>
                                              this.handleChapterMovementUpward(
                                                index,
                                              )
                                            }
                                          >
                                            <ArrowUpward
                                              className={
                                                classes.utilityActionIcon
                                              }
                                              fontSize="small"
                                            />
                                          </NavActionButton>
                                        </div>
                                      </div>
                                    </div>
                                  </ListItem>
                                  {/*
                                   * `Collapse` takes a single child, so list and actions row are
                                   * wrapped in one div. `unmountOnExit` replaces the old
                                   * `{value.isTabOpen && ...}` guard: that guard unmounted the
                                   * children synchronously, leaving the exit transition nothing to
                                   * animate, so closing snapped shut.
                                   */}
                                  <Collapse in={value.isTabOpen} unmountOnExit>
                                    <div>
                                      <List
                                        className={classes.chapterListInner}
                                      >
                                        {value.paragraphData.map(
                                          (item, paragraphIndex) => {
                                            return (
                                              <Fragment key={paragraphIndex}>
                                                <ListItem
                                                  key={paragraphIndex}
                                                  button
                                                  className={[
                                                    classes.navSubItem,
                                                    item.isTabSelected
                                                      ? classes.navSubItemSelected
                                                      : '',
                                                  ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                                  onClick={(event) =>
                                                    this.handleChangeTabWithChapters(
                                                      event,
                                                      paragraphIndex,
                                                      index,
                                                    )
                                                  }
                                                >
                                                  <div
                                                    className={
                                                      classes.navSubItemContentWrap
                                                    }
                                                  >
                                                    <div
                                                      className={
                                                        classes.navSubItemMain
                                                      }
                                                    >
                                                      <div
                                                        className={[
                                                          classes.selectionSlot,
                                                          item.isTabSelected
                                                            ? classes.selectionSlotActive
                                                            : classes.selectionSlotInactive,
                                                        ].join(' ')}
                                                      />
                                                      <div
                                                        className={
                                                          classes.navSubItemContent
                                                        }
                                                      >
                                                        <div
                                                          className={
                                                            classes.navLeadingSlot
                                                          }
                                                        >
                                                          <NavActionButton
                                                            title={
                                                              item.isBlocked
                                                                ? 'Sblocca modifica'
                                                                : 'Blocca modifica'
                                                            }
                                                            className={
                                                              classes.navActionButton
                                                            }
                                                            disabled={
                                                              isConsulente
                                                            }
                                                            onClick={() =>
                                                              this.toggleParagraphLockChapters(
                                                                item,
                                                                index,
                                                              )
                                                            }
                                                          >
                                                            {item.isBlocked ? (
                                                              <Lock
                                                                className={
                                                                  classes.utilityActionIcon
                                                                }
                                                                fontSize="small"
                                                              />
                                                            ) : (
                                                              <LockOpen
                                                                className={
                                                                  classes.utilityActionIcon
                                                                }
                                                                fontSize="small"
                                                              />
                                                            )}
                                                          </NavActionButton>
                                                        </div>
                                                        <StructureNavLabel
                                                          text={item.nomeTab}
                                                          className={
                                                            classes.navSubItemText
                                                          }
                                                          tooltipClassName={
                                                            classes.structureInfoTooltip
                                                          }
                                                        />
                                                        {item.tipo === 'PDF' ? (
                                                          <PictureAsPdfIcon
                                                            className={
                                                              classes.utilityActionIcon
                                                            }
                                                            fontSize="small"
                                                          />
                                                        ) : null}
                                                      </div>
                                                    </div>
                                                    <div
                                                      className={
                                                        classes.navActionsColumn
                                                      }
                                                    >
                                                      {!item.obbligatorio && (
                                                        <div
                                                          className={
                                                            classes.navActionSlot
                                                          }
                                                        >
                                                          <NavActionButton
                                                            title="Elimina paragrafo"
                                                            className={
                                                              classes.navActionButton
                                                            }
                                                            disabled={
                                                              item.isBlocked &&
                                                              isConsulente
                                                            }
                                                            onClick={() =>
                                                              this.handleDeleteParagraphForChapters(
                                                                index,
                                                                paragraphIndex,
                                                              )
                                                            }
                                                          >
                                                            <DeleteOutline
                                                              className={
                                                                classes.destructiveActionIcon
                                                              }
                                                              fontSize="small"
                                                            />
                                                          </NavActionButton>
                                                        </div>
                                                      )}
                                                      <div
                                                        className={
                                                          classes.navMoveGroup
                                                        }
                                                      >
                                                        <NavActionButton
                                                          title="Sposta giù"
                                                          className={
                                                            classes.navMoveButton
                                                          }
                                                          onClick={() =>
                                                            this.handleParagraphMovementDownwardForChapters(
                                                              item,
                                                              index,
                                                              paragraphIndex,
                                                            )
                                                          }
                                                        >
                                                          <ArrowDownward
                                                            className={
                                                              classes.utilityActionIcon
                                                            }
                                                            fontSize="small"
                                                          />
                                                        </NavActionButton>
                                                        <NavActionButton
                                                          title="Sposta su"
                                                          className={
                                                            classes.navMoveButton
                                                          }
                                                          onClick={() =>
                                                            this.handleParagraphMovementUpwardForChapters(
                                                              item,
                                                              index,
                                                              paragraphIndex,
                                                            )
                                                          }
                                                        >
                                                          <ArrowUpward
                                                            className={
                                                              classes.utilityActionIcon
                                                            }
                                                            fontSize="small"
                                                          />
                                                        </NavActionButton>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </ListItem>
                                              </Fragment>
                                            );
                                          },
                                        )}
                                      </List>
                                      <div
                                        className={
                                          classes.chapterParagraphActions
                                        }
                                      >
                                        <div
                                          className={classes.addIconButtonRow}
                                        >
                                          <Tooltip
                                            title="Aggiungi contenuto"
                                            placement="top"
                                            enterTouchDelay={0}
                                          >
                                            <IconButton
                                              className={classes.addIconButton}
                                              aria-label="Aggiungi paragrafo o carica PDF"
                                              aria-haspopup="true"
                                              onClick={(event) =>
                                                this.openAddParagraphMenu(
                                                  event,
                                                  index,
                                                )
                                              }
                                            >
                                              <Add fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </div>
                                      </div>
                                    </div>
                                  </Collapse>
                                </Fragment>
                              );
                            })}
                          </List>
                        </div>
                        <div
                          className={`${classes.navigationActions} ${classes.navigationPanelFooter}`}
                        >
                          <Button
                            color="primary"
                            variant="outlined"
                            className={classes.secondaryActionButton}
                            onClick={this.handleAggiuntaCapitolo}
                          >
                            Aggiungi capitolo
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={classes.navigationScrollArea}>
                          <Tabs
                            className={classes.tabsContainer}
                            variant="standard"
                            orientation="vertical"
                            value={this.state.tabValue}
                            onChange={this.handleTabChange}
                          >
                            {tabs.map((tab, tabIndex) => (
                              <Tab
                                key={tab.value}
                                className={classes.verticalTab}
                                // FIXME: an IconButton inside label/Tab raises a warning
                                label={
                                  <div className={classes.tabLabel}>
                                    <div className={classes.tabLabelContent}>
                                      <div
                                        className={[
                                          classes.selectionSlot,
                                          this.state.tabValue === tabIndex
                                            ? classes.selectionSlotActive
                                            : classes.selectionSlotInactive,
                                        ].join(' ')}
                                      />
                                      <div className={classes.navLeadingSlot}>
                                        <NavActionButton
                                          title={
                                            tab.isBlocked
                                              ? 'Sblocca modifica'
                                              : 'Blocca modifica'
                                          }
                                          className={classes.navActionButton}
                                          disabled={isConsulente}
                                          onClick={() =>
                                            this.handleToggleLock(tab)
                                          }
                                        >
                                          {tab.isBlocked ? (
                                            <Lock
                                              className={
                                                classes.utilityActionIcon
                                              }
                                              fontSize="small"
                                            />
                                          ) : (
                                            <LockOpen
                                              className={
                                                classes.utilityActionIcon
                                              }
                                              fontSize="small"
                                            />
                                          )}
                                        </NavActionButton>
                                      </div>
                                      <StructureNavLabel
                                        text={tab.label}
                                        className={classes.tabLabelText}
                                        tooltipClassName={
                                          classes.structureInfoTooltip
                                        }
                                        typographyProps={{
                                          component: 'span',
                                          display: 'block',
                                          style: {
                                            fontWeight:
                                              this.state.tabValue === tabIndex
                                                ? 700
                                                : 500,
                                          },
                                        }}
                                      />
                                      {tab.tipo === 'PDF' && (
                                        <PictureAsPdfIcon
                                          className={classes.utilityActionIcon}
                                          fontSize="small"
                                        />
                                      )}
                                    </div>
                                    <div className={classes.navActionsColumn}>
                                      {!tab.obbligatorio && (
                                        <div className={classes.navActionSlot}>
                                          <NavActionButton
                                            title="Elimina paragrafo"
                                            className={classes.navActionButton}
                                            disabled={
                                              tab.isBlocked && isConsulente
                                            }
                                            onClick={() =>
                                              this.handleDeleteTab(tab)
                                            }
                                          >
                                            <DeleteOutline
                                              className={
                                                classes.destructiveActionIcon
                                              }
                                              fontSize="small"
                                            />
                                          </NavActionButton>
                                        </div>
                                      )}
                                      <div className={classes.navMoveGroup}>
                                        <NavActionButton
                                          title="Sposta giù"
                                          className={classes.navMoveButton}
                                          onClick={() =>
                                            this.handleMoveDown(tab)
                                          }
                                        >
                                          <ArrowDownward
                                            className={
                                              classes.utilityActionIcon
                                            }
                                            fontSize="small"
                                          />
                                        </NavActionButton>
                                        <NavActionButton
                                          title="Sposta su"
                                          className={classes.navMoveButton}
                                          onClick={() => this.handleMoveUp(tab)}
                                        >
                                          <ArrowUpward
                                            className={
                                              classes.utilityActionIcon
                                            }
                                            fontSize="small"
                                          />
                                        </NavActionButton>
                                      </div>
                                    </div>
                                  </div>
                                }
                              />
                            ))}
                          </Tabs>
                        </div>
                        <div
                          className={`${classes.navigationActions} ${classes.navigationPanelFooter}`}
                        >
                          <Button
                            color="primary"
                            size="small"
                            variant="outlined"
                            className={classes.secondaryActionButton}
                            onClick={() => this.handleParagraphCreation()}
                          >
                            Aggiungi paragrafo
                          </Button>
                          <Button
                            size="small"
                            className={classes.textActionButton}
                            component="label"
                            role={undefined}
                          >
                            Carica PDF
                            <VisuallyHiddenInput
                              type="file"
                              accept="application/pdf"
                              onChange={this.handlePDFLoading}
                            />
                          </Button>
                        </div>
                      </>
                    )}
                    <Menu
                      anchorEl={this.state.addParagraphMenu.anchorEl}
                      open={Boolean(this.state.addParagraphMenu.anchorEl)}
                      onClose={this.closeAddParagraphMenu}
                      getContentAnchorEl={null}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      <MenuItem onClick={this.handleAddParagraphFromMenu}>
                        Aggiungi paragrafo
                      </MenuItem>
                      <MenuItem component="label" role={undefined}>
                        Carica PDF
                        <VisuallyHiddenInput
                          type="file"
                          accept="application/pdf"
                          onChange={this.handleUploadPDFFromMenu}
                        />
                      </MenuItem>
                    </Menu>
                  </div>
                </ThemeProvider>

                <div className={`${classes.panelCard} ${classes.editorPanel}`}>
                  {!this.hasSelectedParagraphForEditor() ||
                  (this.state.chapterMode &&
                    this.state.tabListValues.length === 0) ? (
                    <EmptyManualView
                      chapterMode={Boolean(this.state.chapterMode)}
                      chapterName={this.getPlaceholderChapterName()}
                      hasChapters={this.state.tabListValues.length > 0}
                    />
                  ) : (
                    <>
                      <div className={classes.panelHeader}>
                        <Typography className={classes.panelTitle} variant="h6">
                          {this.state.currentTabValue.tipo === 'PDF'
                            ? 'Gestione allegato PDF'
                            : 'Contenuto del paragrafo'}
                        </Typography>
                      </div>

                      <div className={classes.editorHeader}>
                        {!this.state.currentTabValue.obbligatorio ? (
                          <TextField
                            className={classes.editorTitleField}
                            InputProps={{ disableUnderline: true }}
                            inputProps={{
                              min: 0,
                              style: {
                                textAlign: 'left',
                                fontSize: 30,
                                fontWeight: 700,
                                verticalAlign: 'middle',
                              },
                            }}
                            onChange={(e) =>
                              this.handleParagraphTitleChange(e.target.value)
                            }
                            disabled={!this.state.editParagraphTitle}
                            value={this.state.currentTabValue.label}
                          />
                        ) : (
                          <TextField
                            disabled={!this.state.editParagraphTitle}
                            className={classes.editorTitleField}
                            InputProps={{ disableUnderline: true }}
                            inputProps={{
                              min: 0,
                              style: {
                                textAlign: 'left',
                                fontSize: 30,
                                fontWeight: 700,
                                verticalAlign: 'middle',
                              },
                            }}
                            onChange={(e) =>
                              this.handleParagraphTitleChange(e.target.value)
                            }
                            value={this.state.currentTabValue.label}
                          />
                        )}

                        <div className={classes.editorHeaderActions}>
                          <IconButton
                            size="small"
                            onClick={this.handleToggleEdit}
                            disabled={
                              this.state.currentTabValue.isBlocked &&
                              isConsulente
                            }
                          >
                            <Edit
                              htmlColor={
                                this.state.editParagraphTitle
                                  ? this.props.theme.palette.primary.main
                                  : '#707070'
                              }
                            />
                          </IconButton>
                          {!this.state.currentTabValue.obbligatorio && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                this.handleDeleteParagraphFromMainPanelButton();
                              }}
                            >
                              <CloseIcon
                                className={classes.destructiveActionIcon}
                              />
                            </IconButton>
                          )}
                        </div>
                      </div>

                      {this.state.currentTabValue.tipo !== 'PDF' ? (
                        <div className={classes.sectionBlock}>
                          <div className={classes.sectionHeader}>
                            <Typography
                              className={classes.sectionTitle}
                              variant="h6"
                            >
                              Sottoparagrafi
                            </Typography>
                          </div>
                          {(this.state.sottoParagrafi.length > 0 ||
                            this.state.sottoparagrafiLoading) && (
                            <div className={classes.contentContainer}>
                              <Box className={classes.contentBox}>
                                {this.state.sottoparagrafiLoading ||
                                (this.state.loading &&
                                  this.state.sottoParagrafi.length > 0) ? (
                                  <ContentBlocksSkeleton
                                    aria-label="Caricamento sottoparagrafi"
                                    showFileActionRow
                                  />
                                ) : (
                                  this.state.sottoParagrafi.map((item) => {
                                    return item.value;
                                  })
                                )}
                              </Box>
                            </div>
                          )}
                          {!this.state.loading && this.state.tabValue > -1 && (
                            <div
                              className={classes.contextualActions}
                              aria-busy={this.state.sottoparagrafiLoading}
                            >
                              <Button
                                color="primary"
                                variant="outlined"
                                className={classes.editorActionButton}
                                disabled={this.state.sottoparagrafiLoading}
                                onClick={() => {
                                  this.setState(
                                    {
                                      modalitaSottoparagrafo:
                                        'ImmagineAndTesto',
                                    },
                                    () => {
                                      this.handleAggiuntaSottoparagrafi();
                                    },
                                  );
                                }}
                              >
                                Aggiungi sottoparagrafo
                              </Button>
                              <Button
                                color="primary"
                                variant="outlined"
                                disabled={
                                  this.state.sottoparagrafiLoading ||
                                  (this.state.currentTabValue.isBlocked &&
                                    isConsulente)
                                }
                                className={classes.editorActionButton}
                                onClick={() => {
                                  this.setState((prevState) => ({
                                    isModalOpen: !prevState.isModalOpen,
                                  }));
                                }}
                              >
                                Aggiungi tabella
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : !this.state.loadingPDF ? (
                        <div className={classes.sectionBlock}>
                          <div className={classes.sectionHeader}>
                            <Typography
                              className={classes.sectionTitle}
                              variant="h6"
                            >
                              Anteprima PDF
                            </Typography>
                          </div>
                          <div className={classes.pdfSection}>
                            <div className={classes.pdfActionRow}>
                              <Button
                                startIcon={<Launch />}
                                href={currentPdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="contained"
                                className={`${classes.primaryLinkButton} ${classes.pdfActionRowUniformButton}`}
                              >
                                Visualizza PDF a schermo intero
                              </Button>
                              <Button
                                variant="outlined"
                                component="label"
                                disabled={
                                  this.state.currentTabValue.isBlocked &&
                                  isConsulente
                                }
                                role={undefined}
                                className={`${classes.editorActionButton} ${classes.pdfActionRowUniformButton}`}
                              >
                                Scegli file
                                <VisuallyHiddenInput
                                  type="file"
                                  accept="application/pdf"
                                  disabled={
                                    this.state.currentTabValue.isBlocked &&
                                    isConsulente
                                  }
                                  onChange={
                                    this.handleUpdatePDFInSottoparagrafo
                                  }
                                />
                              </Button>
                            </div>
                            <div className={classes.pdfViewerRow}>
                              <IconButton
                                className={classes.pdfPagerButton}
                                onClick={() => {
                                  this.goToPreviousPDFPage(
                                    this.state.pdfLoadedPage,
                                  );
                                }}
                              >
                                <ArrowBackIosTwoTone
                                  htmlColor={
                                    this.state.pdfLoadedPage <= 1
                                      ? 'gray'
                                      : this.props.theme.palette.secondary.main
                                  }
                                />
                              </IconButton>
                              <div className={classes.pdfPreviewWrapper}>
                                <div className={classes.pdfPreviewFrame}>
                                  <Document
                                    file={currentPdfUrl}
                                    onLoadSuccess={this.onDocumentLoadSuccess}
                                    noData={
                                      <div style={{ height: 842, width: 596 }}>
                                        <div
                                          className={classes.errorPDFPosition}
                                        >
                                          Attenzione, file PDF non trovato
                                        </div>
                                      </div>
                                    }
                                    error={
                                      <div style={{ height: 842, width: 596 }}>
                                        <div
                                          className={classes.errorPDFPosition}
                                        >
                                          Attenzione, errore caricamento file
                                          PDF
                                        </div>
                                      </div>
                                    }
                                    loading={
                                      <div style={{ height: 842, width: 596 }}>
                                        <div
                                          className={classes.errorPDFPosition}
                                        >
                                          <SpinnerComponent size={32} />
                                        </div>
                                      </div>
                                    }
                                  >
                                    <Page
                                      width={596}
                                      loading={
                                        <div
                                          style={{ height: 842, width: 596 }}
                                        >
                                          <div
                                            className={classes.errorPDFPosition}
                                          >
                                            <SpinnerComponent size={32} />
                                          </div>
                                        </div>
                                      }
                                      pageNumber={this.state.pdfLoadedPage}
                                    />
                                  </Document>
                                </div>
                              </div>
                              <IconButton
                                className={classes.pdfPagerButton}
                                onClick={() =>
                                  this.goToNextPDFPage(this.state.pdfLoadedPage)
                                }
                              >
                                <ArrowForwardIosTwoTone
                                  htmlColor={
                                    this.state.pdfLoadedPage >=
                                    this.state.pdfNumberOfPages
                                      ? 'gray'
                                      : this.props.theme.palette.primary.main
                                  }
                                />
                              </IconButton>
                            </div>
                            <Typography
                              className={classes.pdfPageCount}
                              variant="body2"
                            >
                              Pagina {this.state.pdfLoadedPage} di{' '}
                              {this.state.pdfNumberOfPages}
                            </Typography>
                          </div>
                        </div>
                      ) : (
                        <SpinnerComponent size={32} />
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className={classes.footerBar}>
                <div className={classes.footerActions}>
                  <IndietroButtonColorFul
                    secondaryButton
                    alert={this.state.edit}
                    onConfirmedNavigate={skipNextNavigationGuard}
                  />
                  <ActionButton
                    grayVersion={false}
                    disabled={this.state.isSaveButtonPressed}
                    label="Salva Modifiche"
                    onClick={this.handleSave}
                  />
                </div>
              </div>
            </>
          ) : (
            <SpinnerComponent size={24} />
          )}
        </div>
        <CreazioneTabellaModal
          colonneTabellaCustom={this.state.colonneTabellaCustom}
          currentTabValue={this.state.currentTabValue}
          handleAggiuntaTable={this.handleAggiuntaTable}
          onAddColumn={this.onAddColumn}
          onColumnChange={this.onColumnChange}
          onClose={this.onCreazioneTabellaModalClose}
          onDeleteColumn={this.onDeleteColumn}
          open={this.state.isModalOpen}
        />
        {this.state.isChangeChapterNameModalOpen ? (
          <ModalChangeChapterName
            isChangeChapterNameModalOpen={
              this.state.isChangeChapterNameModalOpen
            }
            closeModalToChangeChapterName={this.closeModalToChangeChapterName}
            saveChapterName={this.saveChapterName}
            chapter={this.state.selectedChapter}
          />
        ) : null}
        {this.state.twoActionAlert.isVisible && (
          <TwoActionsAlertDialogForFileUpload
            visible={this.state.twoActionAlert.isVisible}
            title={this.state.twoActionAlert.title}
            indexForCapitolo={this.state.twoActionAlert.indexForCapitolo}
            message={this.state.twoActionAlert.message}
            message2={this.state.twoActionAlert.message2}
            onIndietroClicked={this.abortPDFParagraphCreation}
            onOkClicked={
              this.state.chapterMode
                ? this.createPDFParagraphForCapitolo
                : this.createPDFParagraph
            }
            loading={this.state.twoActionAlert.loading}
          />
        )}
        {this.state.isTwoActionAlertForUpdateVisible && (
          <TwoActionsAlertDialogForFileUpload
            visible={this.state.isTwoActionAlertForUpdateVisible}
            title={'Aggiornamento PDF'}
            indexForCapitolo={null}
            message={
              'Attenzione, stai per aggiornare il PDF. La dimensione consentita è massimo 10MB e il file non sarà editabile. Procedere?'
            }
            message2={
              'Nome file: ' +
              this.state.fileForUpdatePDF.name +
              ' Dimensione file:' +
              Math.floor(this.state.fileForUpdatePDF.size / 1000) +
              ' kb'
            }
            onIndietroClicked={() => {
              this.setState({
                isTwoActionAlertForUpdateVisible: false,
                fileForUpdatePDF: null,
              });
            }}
            onOkClicked={() => {
              this.handleUpdateAPICallPDFInSottoparagrafo();
            }}
            loading={this.state.isTwoActionAlertForUpdateLoading}
          />
        )}
        {this.state.isConadModalOpen && (
          <ErrorDialog
            open={this.state.isConadModalOpen}
            message={
              this.props.mode === 'manuale'
                ? 'Attenzione: questo manuale può essere modificato soltanto attraverso la web app dedicata http://conad-backoffice.biorsaf.it/'
                : 'Attenzione: questo modello può essere modificato soltanto attraverso la web app dedicata http://conad-backoffice.biorsaf.it/'
            }
            onCloseButtonClicked={() => {
              this.handlePushBack();
            }}
          />
        )}
      </div>
    );
  }
}
export default withStyles(styles)(withRouter(CreazioneNuovoManuale));
